import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

const globalStyles = `
  * { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg: #F4F5F7;
    --surface: #FFFFFF;
    --surface2: #F8F9FB;
    --border: rgba(0,0,0,0.08);
    --border-strong: rgba(0,0,0,0.15);
    --text: #1E2022;
    --text2: #52575C;
    --text3: #8A929A;
    --accent: #2563EB;
    --accent-hover: #1D4ED8;
    --accent-bg: #EFF6FF;
    --accent-text: #1E3A8A;
    --success: #16A34A;
    --success-bg: #F0FDF4;
    --success-text: #14532D;
    --warn-bg: #FEF3C7;
    --warn-text: #92400E;
    --danger: #DC2626;
    --danger-bg: #FEF2F2;
    --danger-text: #991B1B;
    --radius: 16px;
    --radius-sm: 10px;
    --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.04);
    --shadow-md: 0 8px 24px rgba(0, 0, 0, 0.08);
    --font: 'Pretendard', 'Noto Sans KR', -apple-system, sans-serif;
  }

  .app-container.dark-mode {
    --bg: #121212;
    --surface: #1E1E1E;
    --surface2: #2A2A2A;
    --border: rgba(255,255,255,0.08);
    --border-strong: rgba(255,255,255,0.15);
    --text: #F3F4F6;
    --text2: #9CA3AF;
    --text3: #6B7280;
    --accent: #3B82F6;
    --accent-hover: #60A5FA;
    --accent-bg: #1E3A8A;
    --accent-text: #DBEAFE;
    --success-bg: #064E3B;
    --success-text: #A7F3D0;
    --warn-bg: #78350F;
    --warn-text: #FDE68A;
    --danger: #7F1D1D;
    --danger-bg: #450A0A;
    --danger-text: #FECACA;
    --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.2);
    --shadow-md: 0 8px 24px rgba(0, 0, 0, 0.4);
  }

  @keyframes viewEnter { 0% { opacity: 0; transform: translateY(15px); } 100% { opacity: 1; transform: translateY(0); } }
  @keyframes modalOverlayEnter { from { opacity: 0; backdrop-filter: blur(0px); } to { opacity: 1; backdrop-filter: blur(4px); } }
  @keyframes modalEnter { 0% { opacity: 0; transform: translateY(100%); } 100% { opacity: 1; transform: translateY(0); } }
  @keyframes pulse-badge { 0%, 100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); } 50% { transform: scale(1.05); opacity: 0.8; box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); } }
  @keyframes popIn { 0% { opacity: 0; transform: scale(0.85); } 100% { opacity: 1; transform: scale(1); } }
  
  .app-container { font-family: var(--font); background: var(--bg); color: var(--text); min-height: 100vh; transition: background-color 0.3s ease, color 0.3s ease; }

  .app-header {
    background: var(--surface); border-bottom: 1px solid var(--border); padding: 16px 20px;
    display: flex; align-items: center; gap: 14px; position: sticky; top: 0; z-index: 100;
    box-shadow: 0 2px 10px rgba(0,0,0,0.02); transition: background-color 0.3s ease, border-color 0.3s ease;
  }
  .app-icon { 
    width: 40px; height: 40px; background: linear-gradient(135deg, var(--accent), #818CF8); 
    border-radius: 12px; display: flex; align-items: center; justify-content: center; 
    font-size: 20px; box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
  }
  .app-title { font-size: 18px; font-weight: 700; letter-spacing: -0.02em; display: flex; align-items: center; gap: 6px; }
  .role-tag { font-size: 11px; padding: 2px 6px; border-radius: 6px; background: var(--accent-bg); color: var(--accent-text); font-weight: 800; }
  .app-subtitle { font-size: 12px; color: var(--text3); margin-top: 2px; font-weight: 500; }

  .icon-btn {
    display: flex; align-items: center; justify-content: center;
    width: 38px; height: 38px; border-radius: 50%;
    background: var(--surface2); border: 1px solid var(--border);
    color: var(--text); font-size: 16px; cursor: pointer; transition: all 0.2s;
  }
  .icon-btn:hover { background: var(--border); transform: scale(1.05); }

  .tabs { display: flex; background: var(--surface); border-bottom: 1px solid var(--border); padding: 0 16px; overflow-x: auto; scrollbar-width: none; transition: background-color 0.3s ease, border-color 0.3s ease; }
  .tabs::-webkit-scrollbar { display: none; }
  .tab { padding: 14px 16px; font-size: 15px; font-weight: 600; color: var(--text3); border-bottom: 3px solid transparent; cursor: pointer; white-space: nowrap; background: none; border-top: none; border-left: none; border-right: none; font-family: var(--font); transition: all 0.25s ease; position: relative; outline: none; }
  .tab:hover { color: var(--text2); }
  .tab.active { color: var(--accent); border-bottom-color: var(--accent); }
  .tab-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 20px; height: 20px; padding: 0 6px; background: var(--danger); color: white; border-radius: 10px; font-size: 11px; font-weight: 800; margin-left: 6px; vertical-align: middle; animation: pulse-badge 2s infinite; }

  .view { padding: 24px 20px; max-width: 720px; margin: 0 auto; animation: viewEnter 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
  .view-wide { padding: 24px 20px; max-width: 1200px; margin: 0 auto; animation: viewEnter 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }

  .card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 24px; margin-bottom: 20px; box-shadow: var(--shadow-sm); transition: box-shadow 0.3s ease, background-color 0.3s ease, border-color 0.3s ease; }
  .card:hover { box-shadow: var(--shadow-md); }

  .section-title { font-size: 13px; font-weight: 700; color: var(--text2); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 16px; display: flex; align-items: center; gap: 6px; }
  .section-title::before { content: ''; display: block; width: 4px; height: 14px; background: var(--accent); border-radius: 2px; }

  label { display: block; font-size: 13px; font-weight: 600; color: var(--text2); margin-bottom: 6px; margin-top: 16px; }
  label:first-of-type { margin-top: 0; }

  input[type="text"], input[type="number"], input[type="date"], select, textarea { width: 100%; padding: 12px 14px; border: 1.5px solid var(--border); border-radius: var(--radius-sm); font-size: 15px; font-family: var(--font); color: var(--text); background: var(--surface2); outline: none; transition: all 0.2s ease; -webkit-appearance: none; }
  input:focus, select:focus, textarea:focus { background: var(--surface); border-color: var(--accent); box-shadow: 0 0 0 4px var(--accent-bg); }
  textarea { resize: vertical; min-height: 90px; line-height: 1.6; }
  .row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

  .btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 12px 20px; border-radius: var(--radius-sm); font-size: 15px; font-weight: 600; cursor: pointer; border: none; font-family: var(--font); transition: all 0.2s ease; }
  .btn:active { transform: scale(0.96); }
  .btn-primary { background: linear-gradient(135deg, var(--accent), #1E40AF); color: white; width: 100%; font-size: 16px; padding: 16px 20px; margin-top: 24px; box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25); }
  .btn-primary:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(37, 99, 235, 0.35); filter: brightness(1.1); }
  .btn-primary:disabled { background: var(--text3); cursor: not-allowed; transform: none; box-shadow: none; filter: none; }
  .btn-outline { background: var(--surface); color: var(--text); border: 1px solid var(--border-strong); }
  .btn-outline:hover { background: var(--surface2); }
  .btn-success { background: var(--success); color: white; box-shadow: 0 4px 12px rgba(22, 163, 74, 0.2); }
  .btn-success:hover { filter: brightness(1.1); box-shadow: 0 6px 16px rgba(22, 163, 74, 0.3); }
  .btn-overdue {
    min-height: 32px;
    padding: 0 12px;
    border-radius: 999px;
    border: 1px solid rgba(146, 64, 14, 0.18);
    background: linear-gradient(180deg, rgba(254, 243, 199, 0.9), rgba(253, 230, 138, 0.7));
    color: var(--warn-text);
    font-size: 12px;
    font-weight: 800;
    letter-spacing: -0.01em;
    white-space: nowrap;
    box-shadow: 0 6px 14px rgba(146, 64, 14, 0.08);
  }
  .btn-overdue:hover {
    background: linear-gradient(180deg, rgba(254, 243, 199, 1), rgba(252, 211, 77, 0.82));
    border-color: rgba(146, 64, 14, 0.28);
    transform: translateY(-1px);
    box-shadow: 0 10px 18px rgba(146, 64, 14, 0.12);
  }
  .app-container.dark-mode .btn-overdue {
    background: linear-gradient(180deg, rgba(120, 53, 15, 0.95), rgba(146, 64, 14, 0.88));
    border-color: rgba(253, 230, 138, 0.18);
    color: #FDE68A;
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.24);
  }
  .app-container.dark-mode .btn-overdue:hover {
    border-color: rgba(253, 230, 138, 0.28);
    box-shadow: 0 12px 22px rgba(0, 0, 0, 0.3);
  }

  .status-badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 700; }
  .status-pending { background: var(--accent-bg); color: var(--accent-text); }
  .status-awaiting { background: var(--warn-bg); color: var(--warn-text); }
  .status-taken-home { background: rgba(14, 116, 144, 0.15); color: #0F766E; }
  .status-overdue { background: var(--danger-bg); color: var(--danger-text); }
  .status-returned { background: var(--success-bg); color: var(--success-text); }
  
  .dot { width: 6px; height: 6px; border-radius: 50%; display: inline-block; }
  .dot-pending { background: var(--accent); animation: pulse-badge 2s infinite; }
  .dot-awaiting { background: var(--warn-text); animation: pulse-badge 2s infinite; }
  .dot-taken-home { background: #0F766E; animation: pulse-badge 2s infinite; }
  .dot-overdue { background: var(--danger); animation: pulse-badge 2s infinite; }
  .dot-returned { background: var(--success); }

  .return-options { display: grid; gap: 10px; margin-top: 8px; }
  .return-option { display: flex; align-items: center; gap: 12px; padding: 16px; border: 1.5px solid var(--border); border-radius: var(--radius-sm); cursor: pointer; background: var(--surface); transition: all 0.2s ease; }
  .return-option:hover { background: var(--surface2); transform: translateY(-1px); }
  .return-option.selected { border-color: var(--accent); background: var(--accent-bg); box-shadow: 0 2px 8px rgba(37, 99, 235, 0.1); }
  .return-option input[type="radio"] { width: 18px; height: 18px; accent-color: var(--accent); flex-shrink: 0; cursor: pointer; }
  .return-option-text { font-size: 15px; font-weight: 600; color: var(--text); }
  .option-grid { display: grid; gap: 10px; margin-top: 16px; }
  .option-chip {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 1.5px solid var(--border);
    border-radius: 14px;
    background: var(--surface2);
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .option-chip:hover { border-color: var(--border-strong); background: var(--surface); transform: translateY(-1px); }
  .option-chip.selected { border-color: var(--accent); background: var(--accent-bg); box-shadow: 0 4px 12px rgba(37, 99, 235, 0.12); }
  .option-chip input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
  .option-chip-mark {
    width: 18px;
    height: 18px;
    border-radius: 999px;
    border: 1.5px solid var(--border-strong);
    background: var(--surface);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s ease;
  }
  .option-chip.selected .option-chip-mark {
    border-color: var(--accent);
    background: var(--accent);
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
  }
  .option-chip.selected .option-chip-mark::after {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: white;
  }
  .option-chip-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
  .option-chip-title { font-size: 14px; font-weight: 700; color: var(--text); line-height: 1.3; }
  .option-chip-sub { font-size: 12px; color: var(--text3); line-height: 1.4; }

  .dash-toolbar { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; justify-content: space-between; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 16px 20px; margin-bottom: 20px; box-shadow: var(--shadow-sm); }
  .dash-toolbar-group { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
  .dash-toggle-btn { padding: 8px 14px; border-radius: 20px; font-size: 13px; font-weight: 600; border: 1px solid var(--border); background: var(--surface2); color: var(--text2); cursor: pointer; transition: all 0.2s ease; }
  .dash-toggle-btn:hover { background: var(--border); }
  .dash-toggle-btn.active { background: var(--text); color: var(--bg); border-color: var(--text); }
  .app-container.dark-mode .dash-toggle-btn.active { background: var(--accent); color: white; border-color: var(--accent); }
  
  .dash-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 16px; }
  .dash-col { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-sm); display: flex; flex-direction: column; overflow: hidden; box-shadow: var(--shadow-sm); }
  .dash-col-header { background: var(--surface2); padding: 12px; text-align: center; font-weight: 800; border-bottom: 1px solid var(--border); color: var(--text); font-size: 15px; }
  .dash-col-body { padding: 8px; display: flex; flex-direction: column; gap: 4px; max-height: 600px; overflow-y: auto; }
  .dash-student { display: flex; justify-content: space-between; align-items: center; padding: 8px 10px; border-radius: 6px; font-size: 13px; font-weight: 600; background: var(--bg); border: 1px solid transparent; transition: all 0.2s; }
  .dash-student.applied { background: var(--surface2); border-color: var(--border); }
  .dash-student.highlight-personal { border-color: var(--accent); background: var(--accent-bg); }
  .dash-st-name { color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 70px; }
  .dash-st-none { color: var(--text3); font-weight: 400; }
  
  .dash-st-status { font-size: 11px; padding: 2px 6px; border-radius: 10px; }
  .dash-st-status.none { background: transparent; color: var(--text3); }
  .dash-st-status.pending { background: var(--accent-bg); color: var(--accent-text); }
  .dash-st-status.awaiting { background: var(--warn-bg); color: var(--warn-text); font-weight: 800; }
  .dash-st-status.taken_home { background: rgba(14, 116, 144, 0.15); color: #0F766E; font-weight: 800; }
  .dash-st-status.overdue { background: var(--danger-bg); color: var(--danger-text); font-weight: 800; }
  .dash-st-status.returned { background: var(--success-bg); color: var(--success-text); }

  .record-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 16px 20px; cursor: pointer; transition: all 0.25s ease; margin-bottom: 12px; box-shadow: var(--shadow-sm); }
  .record-card:hover { transform: translateY(-3px) scale(1.01); box-shadow: var(--shadow-md); border-color: var(--border-strong); }
  .record-card.highlight-pending { border-left: 4px solid var(--accent); background: linear-gradient(to right, var(--accent-bg) 0%, var(--surface) 20%); }
  .app-container.dark-mode .record-card.highlight-pending { background: linear-gradient(to right, rgba(37,99,235,0.1) 0%, var(--surface) 20%); }
  .record-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
  .record-top-side { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
  .record-name { font-size: 16px; font-weight: 700; display: flex; align-items: center; gap: 8px; }
  .record-meta { font-size: 13px; font-weight: 500; color: var(--text3); margin-top: 2px; }
  .record-purpose { font-size: 14px; color: var(--text2); margin-top: 8px; line-height: 1.5; }
  .record-footer { display: flex; align-items: center; justify-content: space-between; margin-top: 14px; padding-top: 12px; border-top: 1px dashed var(--border); }
  .record-time { font-size: 12px; color: var(--text3); font-weight: 500; }

  .teacher-banner { background: var(--danger-bg); border: 1px solid rgba(220,38,38,0.2); border-radius: var(--radius); padding: 16px 20px; margin-bottom: 20px; display: none; box-shadow: 0 4px 12px rgba(220,38,38,0.08); }
  .teacher-banner.visible { display: flex; align-items: flex-start; gap: 12px; animation: viewEnter 0.4s ease; }
  .teacher-banner-icon { font-size: 22px; flex-shrink: 0; }
  .teacher-banner-title { font-size: 15px; font-weight: 700; color: var(--danger-text); margin-bottom: 4px; }
  .teacher-banner-sub { font-size: 13px; font-weight: 500; color: var(--danger-text); opacity: 0.9; line-height: 1.4; }

  .empty-state { text-align: center; padding: 60px 20px; color: var(--text3); }
  .empty-icon { font-size: 42px; margin-bottom: 16px; filter: grayscale(1) opacity(0.5); transition: all 0.3s ease; }
  .empty-state:hover .empty-icon { filter: grayscale(0) opacity(1); transform: scale(1.1); }
  .empty-text { font-size: 16px; font-weight: 600; margin-bottom: 6px; color: var(--text2); }
  .empty-sub { font-size: 14px; }

  .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-end; justify-content: center; animation: modalOverlayEnter 0.3s ease-out forwards; backdrop-filter: blur(6px); }
  .modal { background: var(--surface); border-radius: 24px 24px 0 0; padding: 24px 24px 36px; width: 100%; max-width: 720px; max-height: 90vh; overflow-y: auto; animation: modalEnter 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards; box-shadow: 0 -10px 40px rgba(0,0,0,0.15); }
  .modal-handle { width: 40px; height: 5px; background: var(--border-strong); border-radius: 3px; margin: 0 auto 24px; transition: background 0.2s; }
  .modal-handle:hover { background: var(--text3); }
  .modal-title { font-size: 20px; font-weight: 800; margin-bottom: 4px; }
  .modal-sub { font-size: 14px; font-weight: 500; color: var(--text3); margin-bottom: 24px; }
  .info-row { display: flex; justify-content: space-between; align-items: baseline; padding: 12px 0; border-bottom: 1px solid var(--border); font-size: 15px; }
  .info-row:last-child { border-bottom: none; }
  .info-label { color: var(--text3); font-weight: 600; flex-shrink: 0; margin-right: 12px; }
  .info-value { font-weight: 600; text-align: right; color: var(--text); }
  .btn-row { display: flex; gap: 12px; margin-top: 28px; }
  .btn-row .btn { flex: 1; }

  .alert { padding: 14px 16px; border-radius: var(--radius-sm); font-size: 14px; font-weight: 500; margin-bottom: 20px; display: flex; gap: 8px; align-items: center; }
  .alert-info { background: var(--accent-bg); color: var(--accent-text); border: 1px solid rgba(37,99,235,0.15); }

  .filter-bar { display: flex; gap: 10px; margin-bottom: 16px; overflow-x: auto; padding-bottom: 8px; scrollbar-width: none; }
  .filter-chip { padding: 8px 16px; border-radius: 20px; font-size: 14px; font-weight: 600; border: 1px solid var(--border); cursor: pointer; white-space: nowrap; background: var(--surface); color: var(--text2); transition: all 0.2s ease; box-shadow: 0 1px 3px rgba(0,0,0,0.02); }
  .filter-chip:hover { background: var(--surface2); transform: translateY(-1px); }
  .filter-chip.active { background: var(--text); color: var(--bg); border-color: var(--text); box-shadow: 0 4px 10px rgba(0,0,0,0.1); }
  .app-container.dark-mode .filter-chip.active { background: var(--accent); color: white; border-color: var(--accent); }

  .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 24px; }
  .summary-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 16px 12px; text-align: center; box-shadow: var(--shadow-sm); transition: transform 0.2s ease; }
  .summary-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
  .summary-num { font-size: 28px; font-weight: 800; line-height: 1; margin-bottom: 4px; }
  .summary-label { font-size: 12px; font-weight: 600; color: var(--text3); }
  .summary-num.pulse { animation: num-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
  @keyframes num-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.6; transform: scale(0.95); } }

  .toast { position: fixed; bottom: 32px; left: 50%; transform: translateX(-50%) translateY(100px) scale(0.9); background: rgba(30, 32, 34, 0.95); backdrop-filter: blur(8px); color: white; padding: 14px 24px; border-radius: 30px; box-shadow: 0 10px 30px rgba(0,0,0,0.2); font-size: 15px; font-weight: 600; z-index: 500; transition: all 0.4s ease; white-space: nowrap; pointer-events: none; opacity: 0; }
  .toast.show { transform: translateX(-50%) translateY(0) scale(1); opacity: 1; }
  .period-tag { display: inline-flex; align-items: center; padding: 3px 8px; border-radius: 8px; font-size: 11px; font-weight: 700; background: var(--surface2); color: var(--text2); margin-left: 8px; border: 1px solid var(--border); }

  .roster-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)); gap: 10px; margin-top: 16px; }
  .roster-cell { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 10px; display: flex; flex-direction: column; gap: 6px; box-shadow: var(--shadow-sm); transition: all 0.2s; }
  .roster-cell:focus-within { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-bg); }
  .roster-cell-header { font-size: 12px; font-weight: 700; color: var(--text3); }
  .roster-input { width: 100%; border: none; background: transparent; font-size: 15px; font-weight: 600; color: var(--text); outline: none; padding: 0; font-family: var(--font); }
  .roster-input::placeholder { color: var(--border-strong); font-weight: 400; }
  .roster-add-cell { border: 2px dashed var(--border-strong); background: transparent; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; font-weight: 700; color: var(--text3); border-radius: var(--radius-sm); transition: all 0.2s ease; min-height: 64px; }
  .roster-add-cell:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-bg); }

  /* 통계 분석 화면용 스타일 */
  .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px; }
  .stat-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 24px; box-shadow: var(--shadow-sm); transition: transform 0.2s; }
  .stat-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
  .stat-card-title { font-size: 16px; font-weight: 800; color: var(--text); margin-bottom: 24px; display: flex; align-items: center; gap: 8px; }
  
  .h-bar-wrap { display: flex; flex-direction: column; gap: 14px; }
  .h-bar-row { display: flex; flex-direction: column; gap: 6px; }
  .h-bar-label { font-size: 13px; font-weight: 700; color: var(--text2); display: flex; justify-content: space-between; }
  .h-bar-track { width: 100%; height: 10px; background: var(--surface2); border-radius: 6px; overflow: hidden; }
  .h-bar-fill { height: 100%; background: linear-gradient(90deg, var(--accent), #818CF8); border-radius: 6px; transition: width 1s cubic-bezier(0.16, 1, 0.3, 1); }
  
  /* 리캡 스토리 화면용 애니메이션 스타일 */
  @keyframes shimmer { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
  
  /* x異??띿뒪??留덊궎 ?좊땲硫붿씠??*/
  @keyframes scrollX { from { transform: translateX(10%); } to { transform: translateX(-150%); } }
  @keyframes scrollXRev { from { transform: translateX(-150%); } to { transform: translateX(10%); } }

  .story-modal {
    width: 100%; max-width: 420px; height: 85vh; max-height: 800px;
    background: #0f0f13; color: white; border-radius: 24px;
    position: relative; overflow: hidden; display: flex; flex-direction: column;
    box-shadow: 0 10px 50px rgba(0,0,0,0.8);
    animation: popIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .story-progress { display: flex; gap: 6px; padding: 16px 16px 10px; position: absolute; top: 0; left: 0; right: 0; z-index: 20; }
  .story-bar { flex: 1; height: 3px; background: rgba(255,255,255,0.25); border-radius: 2px; overflow: hidden; }
  .story-fill { height: 100%; background: #FFFFFF; width: 0%; transition: width 0.3s ease; }
  .story-fill.completed { width: 100%; }
  
  .story-fill.active:not(.paused) { width: 100%; animation: fillUp 4.5s linear forwards; }
  .story-fill.active.paused { animation-play-state: paused; }
  @keyframes fillUp { 0% {width: 0%;} 100% {width: 100%;} }

  .story-content { flex: 1; display: flex; flex-direction: column; justify-content: center; padding: 32px 24px; position: relative; z-index: 5; height: 100%; }
  
  .recap-title-text { 
    font-size: 36px; font-weight: 900; text-align: center; margin-bottom: 16px;
    background: linear-gradient(135deg, #FF6B6B 0%, #2563EB 50%, #845EC2 100%);
    background-size: 200% auto; animation: shimmer 3s linear infinite;
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; 
  }
  
  .marquee-container { width: 100%; overflow: hidden; margin-bottom: 24px; position: relative; }
  .marquee-container::after { content: absolute; inset: 0; background: linear-gradient(90deg, #0f0f13 0%, transparent 10%, transparent 90%, #0f0f13 100%); pointer-events: none; }
  .marquee-line { display: flex; gap: 16px; margin-bottom: 12px; width: max-content; animation: scrollX 20s linear infinite; }
  .marquee-line:nth-child(even) { animation: scrollXRev 25s linear infinite; }
  .story-modal.paused .marquee-line { animation-play-state: paused; }
  .marquee-tag { background: rgba(255,255,255,0.1); padding: 8px 16px; border-radius: 20px; font-size: 15px; font-weight: 600; color: rgba(255,255,255,0.9); white-space: nowrap; border: 1px solid rgba(255,255,255,0.05); }

  .chart-bar-wrap { display: flex; align-items: flex-end; justify-content: space-around; height: 220px; padding-top: 20px; border-bottom: 1px solid var(--border); width: 100%; }
  .chart-bar-col { display: flex; flex-direction: column; align-items: center; justify-content: flex-end; gap: 8px; width: 13%; height: 100%; }
  .chart-bar-fill { width: 100%; background: linear-gradient(to top, var(--accent), #845EC2); border-radius: 6px 6px 0 0; min-height: 4px; transition: height 1s cubic-bezier(0.16, 1, 0.3, 1); box-shadow: 0 4px 12px rgba(37,99,235,0.2); }
  .chart-bar-label { font-size: 13px; font-weight: 700; color: var(--text3); margin-top: 4px; text-align: center; word-break: keep-all; }
  .chart-bar-val { font-size: 14px; font-weight: 800; color: var(--accent); margin-bottom: 4px; }
  
  .recap-rank-row { display: flex; align-items: center; justify-content: space-between; padding: 16px; background: rgba(255,255,255,0.1); border-radius: 16px; margin-bottom: 12px; backdrop-filter: blur(8px); }
  .recap-rank-row.me { background: rgba(37,99,235,0.3); border: 1px solid #3B82F6; box-shadow: 0 4px 16px rgba(37,99,235,0.3); transform: scale(1.03); }
  .rank-num { font-size: 20px; font-weight: 900; width: 32px; color: rgba(255,255,255,0.5); }
  .rank-num.gold { color: #FCD34D; text-shadow: 0 0 10px rgba(252,211,77,0.5); } .rank-num.silver { color: #D1D5DB; } .rank-num.bronze { color: #D97706; }
  .rank-name { font-size: 16px; font-weight: 700; flex: 1; color: white; }
  .rank-time { font-size: 15px; font-weight: 800; color: #93C5FD; }

  .recap-pie { width: 200px; height: 200px; border-radius: 50%; box-shadow: 0 0 30px rgba(0,0,0,0.1); transform: rotate(-90deg); margin: 0 auto 30px; }
  .story-modal .recap-pie { box-shadow: 0 0 30px rgba(0,0,0,0.5); }
  .legend-item { display: flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 600; color: var(--text); margin-bottom: 12px; }
  .story-modal .legend-item { color: rgba(255,255,255,0.9); }
  .legend-color { width: 16px; height: 16px; border-radius: 4px; }

  .floating-container { position: absolute; inset: 0; pointer-events: none; overflow: hidden; z-index: 1; }
  .float-item {
    position: absolute; bottom: -150px;
    background: rgba(255,255,255,0.12); backdrop-filter: blur(8px);
    padding: 12px; border-radius: 16px; color: white;
    animation: floatUp linear infinite; display: flex; flex-direction: column; gap: 8px;
    max-width: 160px; box-shadow: 0 4px 16px rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.05);
    will-change: transform, opacity;
  }
  .float-item span { font-size: 12px; line-height: 1.4; word-break: break-all; text-shadow: 0 2px 4px rgba(0,0,0,0.5); }
  .story-modal.paused .float-item { animation-play-state: paused; }
  @keyframes floatUp {
    0% { transform: translateY(150px) scale(0.9); opacity: 0; }
    5% { opacity: 1; }
    95% { opacity: 1; }
    100% { transform: translateY(-110vh) scale(1.1); opacity: 0; }
  }

  .app-footer {
    text-align: center;
    padding: 32px 20px 40px;
    margin-top: 20px;
    border-top: 1px solid var(--border);
    color: var(--text3);
    font-size: 12px;
    line-height: 1.6;
  }
  .app-footer p { margin-bottom: 6px; }
  .app-footer p:last-child { margin-bottom: 0; }

  @media (max-width: 480px) { .row2 { grid-template-columns: 1fr; } .view, .view-wide { padding: 16px; } .roster-grid { grid-template-columns: repeat(3, 1fr); } }
`;

const API_URL = "https://backend-api.dj-892.workers.dev/api/records";
const ROSTER_API_URL = "https://backend-api.dj-892.workers.dev/api/students";

const fetchWithTimeout = async (url, options = {}, timeoutMs = 5000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);
    return response;
  } finally {
    clearTimeout(id);
  }
};

const getLocalYMD = () => {
  const d = new Date();
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().split('T')[0];
};

const getYMDFromIso = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().split('T')[0];
};

const isActiveRecordStatus = (status) => ['pending', 'awaiting_approval', 'taken_home'].includes(status);

const getLocalDayBounds = (ymd = getLocalYMD()) => {
  const start = new Date(`${ymd}T00:00:00`);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return {
    submittedAfter: start.toISOString(),
    submittedBefore: end.toISOString(),
  };
};

const buildTeacherLiveRecordsUrl = (ymd) => {
  const { submittedAfter, submittedBefore } = getLocalDayBounds(ymd);
  const url = new URL(API_URL);
  url.searchParams.set('scope', 'teacher-live');
  url.searchParams.set('submittedAfter', submittedAfter);
  url.searchParams.set('submittedBefore', submittedBefore);
  return url.toString();
};

const sortRecordsBySubmittedAt = (list) => {
  return [...list].sort((a, b) => {
    const timeA = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
    const timeB = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
    return timeA - timeB;
  });
};

const getRecordSyncTime = (record) => {
  if (!record) return 0;
  const baseTime = record.updatedAt || record.returnedAt || record.submittedAt;
  const parsed = baseTime ? new Date(baseTime).getTime() : 0;
  return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizeStudentName = (name) => (name || '').trim().toLowerCase();

const doesRecordMatchStudent = (record, cls, num, name) => (
  record?.cls === cls &&
  String(record?.num) === String(num) &&
  normalizeStudentName(record?.name) === normalizeStudentName(name)
);

const dedupeRecordsById = (list) => {
  const byId = new Map();

  list.forEach((record) => {
    if (!record?.id) return;
    const current = byId.get(record.id);
    if (!current || getRecordSyncTime(record) >= getRecordSyncTime(current)) {
      byId.set(record.id, record);
    }
  });

  return sortRecordsBySubmittedAt(Array.from(byId.values()));
};

const mergeServerSnapshotRecords = (currentRecords, incomingRecords, pendingRecordIds = new Set(), pendingDeletedRecordIds = new Set()) => {
  const merged = new Map();

  incomingRecords.forEach((record) => {
    if (!pendingDeletedRecordIds.has(record.id)) {
      merged.set(record.id, record);
    }
  });

  currentRecords.forEach((record) => {
    if (pendingDeletedRecordIds.has(record.id)) {
      merged.delete(record.id);
      return;
    }

    if (!pendingRecordIds.has(record.id)) return;

    const serverRecord = merged.get(record.id);
    if (!serverRecord || getRecordSyncTime(record) >= getRecordSyncTime(serverRecord)) {
      merged.set(record.id, record);
    }
  });

  return dedupeRecordsById(Array.from(merged.values()));
};

const mergeTeacherLiveRecords = (currentRecords, incomingRecords, targetYmd, pendingRecordIds = new Set(), pendingDeletedRecordIds = new Set()) => {
  const preserved = currentRecords.filter((record) => getYMDFromIso(record.submittedAt) !== targetYmd);
  const mergedToday = new Map();

  incomingRecords.forEach((record) => {
    if (!pendingDeletedRecordIds.has(record.id)) {
      mergedToday.set(record.id, record);
    }
  });

  currentRecords
    .filter((record) => getYMDFromIso(record.submittedAt) === targetYmd)
    .forEach((record) => {
      if (pendingDeletedRecordIds.has(record.id)) {
        mergedToday.delete(record.id);
        return;
      }
      if (!pendingRecordIds.has(record.id)) return;

      const serverRecord = mergedToday.get(record.id);
      if (!serverRecord || getRecordSyncTime(record) >= getRecordSyncTime(serverRecord)) {
        mergedToday.set(record.id, record);
      }
    });

  currentRecords
    .filter((record) => getYMDFromIso(record.submittedAt) === targetYmd)
    .forEach((record) => {
      if (!pendingRecordIds.has(record.id) && !pendingDeletedRecordIds.has(record.id) && !mergedToday.has(record.id)) {
        mergedToday.set(record.id, record);
      }
    }
  );

  return dedupeRecordsById([...preserved, ...mergedToday.values()]);
};

const normalizeStudent = (student) => ({
  id: student.id || `${student.cls}-${student.num}`,
  cls: student.cls,
  num: String(student.num),
  name: (student.name || '').trim(),
});

const normalizeRecord = (rec) => {
  const submittedYmd = getYMDFromIso(rec.submittedAt);
  const todayYmd = getLocalYMD();
  const shouldMarkOverdue =
    ['pending', 'awaiting_approval'].includes(rec.status) &&
    submittedYmd &&
    submittedYmd < todayYmd;

  return {
    ...rec,
    status: shouldMarkOverdue ? 'overdue' : rec.status,
    isPersonalPad: rec.isPersonalPad === true || String(rec.isPersonalPad).toLowerCase() === 'true'
  };
};

// 이미지 관련 레거시 표식은 무시하고 텍스트만 파싱합니다.
const parseDetail = (rawDetail) => {
  if (!rawDetail) return { text: '' };
  
  try {
    const parsed = JSON.parse(rawDetail);
    if (parsed && typeof parsed === 'object' && ('text' in parsed)) {
      return { text: parsed.text || '' };
    }
    throw new Error("Old format fallback");
  } catch (e) {
    let text = rawDetail;
    if (text.includes('[DEVICE_ID]')) text = text.split('[DEVICE_ID]')[0] || '';
    return { text };
  }
};

const analyzeSubject = (text) => {
  if (!text) return '기타';
  if (text.includes('국어')) return '국어';
  if (text.includes('수학')) return '수학';
  if (text.includes('영어')) return '영어';
  if (text.includes('사회')) return '사회';
  if (text.includes('과학')) return '과학';
  if (text.includes('역사') || text.includes('한국사')) return '역사';
  return '기타';
};

const formatDurationText = (ms) => {
  if (!ms || isNaN(ms) || ms < 0) return '0분';
  const mins = Math.floor(ms / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0) return h + '시간 ' + m + '분';
  return m + '분';
};

const formatTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const getUsageDuration = (start, end) => {
  if (!start || !end) return '-';
  const ms = new Date(end) - new Date(start);
  if (ms < 0 || isNaN(ms)) return '-';
  return formatDurationText(ms);
};

const PIE_COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#F43F5E', '#F59E0B', '#10B981'];
const makeConicGradient = (counts, customColors = null) => {
  if (!counts) return 'conic-gradient(var(--surface2) 0% 100%)';
  const total = Object.values(counts).reduce((a,b)=>a+b,0);
  if(total===0) return 'conic-gradient(var(--surface2) 0% 100%)';
  let grad = []; let start = 0;
  Object.keys(counts).forEach((k, i) => {
    const pct = (counts[k]/total)*100;
    let color;
    if (customColors && !Array.isArray(customColors)) {
      color = customColors[k] || '#888';
    } else {
      const colorArr = Array.isArray(customColors) ? customColors : PIE_COLORS;
      color = colorArr[i % colorArr.length];
    }
    grad.push(`${color} ${start}% ${start+pct}%`);
    start += pct;
  });
  return `conic-gradient(${grad.join(', ')})`;
};

const YAJA_COLORS = {
  '야자 1부': '#3B82F6',
  '야자 2부': '#8B5CF6',
  '야자 1·2부 모두': '#F59E0B'
};
const RECORD_SYNC_GRACE_MS = 12000;
const STUDENT_PROFILE_STORAGE_KEY = 'padStudentProfile';

export default function App() {
  const [role] = useState(() => {
    const path = window.location.pathname;
    if (path.startsWith('/trh')) return 'teacher';
    return 'student'; 
  });

  const [activeTab, setActiveTab] = useState(() => {
    return role === 'teacher' ? 'dashboard' : 'submit';
  });
  
  const [isDarkMode, setIsDarkMode] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [records, setRecords] = useState([]);
  const [students, setStudents] = useState([]);
  const [maxSlots, setMaxSlots] = useState({ '1반': 24, '2반': 24, '3반': 24, '4반': 24, '5반': 24, '6반': 24 });

  const [rosterDraft, setRosterDraft] = useState([]);
  const [selectedRosterClass, setSelectedRosterClass] = useState('1반');

  const [submitForm, setSubmitForm] = useState({ 
    cls: '', num: '', name: '', purposeType: '', detail: '', duration: '', isPersonalPad: false 
  });
  const [statusForm, setStatusForm] = useState({ cls: '', num: '', name: '' });
  const [rememberStudentProfile, setRememberStudentProfile] = useState(false);
  const [foundRecord, setFoundRecord] = useState(null);
  const [returnMethod, setReturnMethod] = useState('');
  
  const [currentFilter, setCurrentFilter] = useState('all');
  const [listSortType, setListSortType] = useState('time'); 
  const [listSortOrder, setListSortOrder] = useState('desc'); 

  const [modalState, setModalState] = useState({ type: null, recordId: null });
  const [editForm, setEditForm] = useState({ purposeType: '', detail: '', duration: '' });
  const [toastMsg, setToastMsg] = useState({ message: '', show: false });

  const [recapModal, setRecapModal] = useState({ show: false, data: null, pendingRecord: null });
  const [recapSlide, setRecapSlide] = useState(0);
  const [isStoryPaused, setIsStoryPaused] = useState(false);
  const [studentNoticeModal, setStudentNoticeModal] = useState({ show: false, title: '', message: '' });

  const [approverNames, setApproverNames] = useState({});
  const pendingRecordMetaRef = useRef(new Map());
  const applyRecords = useCallback((updater) => {
    setRecords((prev) => {
      const nextRecords = typeof updater === 'function' ? updater(prev) : updater;
      return dedupeRecordsById(nextRecords);
    });
  }, []);

  const [dashDate, setDashDate] = useState(getLocalYMD());
  const [dashSort, setDashSort] = useState('num'); 
  const [dashDisplay, setDashDisplay] = useState('num'); 
  const [dashShowStatus, setDashShowStatus] = useState(false); 

  const showToast = useCallback((message) => {
    setToastMsg({ message, show: true });
    setTimeout(() => setToastMsg((prev) => ({ ...prev, show: false })), 2400);
  }, []);

  const saveRememberedStudentProfile = useCallback((profile) => {
    if (!rememberStudentProfile) return;
    const nextProfile = {
      cls: profile?.cls || '',
      num: String(profile?.num || ''),
      name: profile?.name || '',
    };
    if (!nextProfile.cls || !nextProfile.num || !nextProfile.name.trim()) return;
    localStorage.setItem(STUDENT_PROFILE_STORAGE_KEY, JSON.stringify(nextProfile));
  }, [rememberStudentProfile]);

  const clearRememberedStudentProfile = useCallback(() => {
    localStorage.removeItem(STUDENT_PROFILE_STORAGE_KEY);
  }, []);

  const rememberCurrentStudentProfile = useCallback((fallbackProfile = null) => {
    const profile = fallbackProfile || {
      cls: submitForm.cls || statusForm.cls,
      num: submitForm.num || statusForm.num,
      name: submitForm.name || statusForm.name,
    };
    saveRememberedStudentProfile(profile);
  }, [saveRememberedStudentProfile, statusForm.cls, statusForm.name, statusForm.num, submitForm.cls, submitForm.name, submitForm.num]);

  const getPendingRecordSets = useCallback(() => {
    const now = Date.now();
    const pendingIds = new Set();
    const pendingDeletedIds = new Set();

    pendingRecordMetaRef.current.forEach((meta, id) => {
      if (!meta || meta.until <= now) {
        pendingRecordMetaRef.current.delete(id);
        return;
      }
      pendingIds.add(id);
      if (meta.deleted) pendingDeletedIds.add(id);
    });

    return { pendingIds, pendingDeletedIds };
  }, []);

  const todayStr = getLocalYMD();
  const unresolvedLatestRecords = useMemo(() => {
    const latestByStudent = new Map();

    records.forEach((record) => {
      if (!['pending', 'awaiting_approval', 'taken_home', 'overdue'].includes(record.status)) return;
      const studentKey = `${record.cls}-${record.num}-${(record.name || '').trim().toLowerCase()}`;
      const current = latestByStudent.get(studentKey);
      if (!current || getRecordSyncTime(record) > getRecordSyncTime(current)) {
        latestByStudent.set(studentKey, record);
      }
    });

    return Array.from(latestByStudent.values());
  }, [records]);

  const { pendingCount, todayUniqueCount, teacherApprovalCount, totalPendingCount } = useMemo(() => {
    let pending = 0;
    const uniqueSet = new Set();

    records.forEach(r => {
      if (getYMDFromIso(r.submittedAt) === todayStr) {
        if (r.status === 'pending') pending++;
        uniqueSet.add(`${r.cls}-${r.num}`);
      }
    });

    return {
      pendingCount: pending,
      todayUniqueCount: uniqueSet.size,
      teacherApprovalCount: records.filter(r => r.status === 'awaiting_approval').length,
      totalPendingCount: unresolvedLatestRecords.filter(r => r.status === 'pending').length
    };
  }, [records, todayStr, unresolvedLatestRecords]);

  const statsData = useMemo(() => {
    let totalMs = 0;
    let returnedCount = 0;
    let personalCount = 0;
    const subjectCounts = { '국어':0, '수학':0, '영어':0, '사회':0, '과학':0, '역사':0, '기타':0 };
    const durationCounts = { '야자 1부':0, '야자 2부':0, '야자 1·2부 모두':0 };
    const classTimeMs = { '1반':0, '2반':0, '3반':0, '4반':0, '5반':0, '6반':0 };
    const purposeCounts = {};

    records.forEach(r => {
      if (r.isPersonalPad) personalCount++;
      durationCounts[r.duration] = (durationCounts[r.duration] || 0) + 1;
      purposeCounts[r.purposeType] = (purposeCounts[r.purposeType] || 0) + 1;

      const parsed = parseDetail(r.detail);
      const subj = analyzeSubject(parsed.text);
      subjectCounts[subj]++;

      if (r.status === 'returned' && r.returnedAt && r.submittedAt) {
        const ms = new Date(r.returnedAt) - new Date(r.submittedAt);
        if (ms > 0) {
          totalMs += ms;
          returnedCount++;
          classTimeMs[r.cls] = (classTimeMs[r.cls] || 0) + ms;
        }
      }
    });

    const totalCount = records.length;
    return {
      totalCount,
      personalRatio: totalCount > 0 ? ((personalCount / totalCount) * 100).toFixed(1) : 0,
      avgMs: returnedCount > 0 ? Math.floor(totalMs / returnedCount) : 0,
      subjectCounts,
      durationCounts,
      classTimeMs,
      purposeCounts
    };
  }, [records]);

  const dashRecordsMap = useMemo(() => {
    const map = new Map();
    records.forEach(r => {
      if (getYMDFromIso(r.submittedAt) === dashDate) {
        map.set(`${r.cls}-${r.num}`, r);
      }
    });
    return map;
  }, [records, dashDate]);

  const executeSafePut = async (recordToUpdate, oldRecord) => {
    try {
      const response = await fetchWithTimeout(API_URL, { method: "GET" });
      const data = await response.json();
      const serverRec = data.find(r => r.id === recordToUpdate.id);
      
      const serverTime = serverRec && serverRec.updatedAt ? new Date(serverRec.updatedAt).getTime() : 0;
      const localTime = oldRecord && oldRecord.updatedAt ? new Date(oldRecord.updatedAt).getTime() : 0;

      if (serverRec && serverTime > localTime) {
        showToast('방금 다른 곳에서 먼저 수정되었어요. 최신 상태로 다시 불러옵니다.');
        applyRecords(data.map(normalizeRecord));
        return false;
      }

      await fetchWithTimeout(API_URL, { 
        method: "PUT", 
        headers: { "Content-Type": "application/json" }, 
        body: JSON.stringify(recordToUpdate) 
      });
      return true;
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    const fetchRecords = async ({ teacherLive = false } = {}) => {
      try {
        const requestUrl = teacherLive
          ? buildTeacherLiveRecordsUrl(getLocalYMD())
          : API_URL;
        const res = await fetchWithTimeout(requestUrl);
        const data = await res.json();
        
        if (Array.isArray(data)) { 
          const normalized = data.map(normalizeRecord);
          const { pendingIds, pendingDeletedIds } = getPendingRecordSets();
          applyRecords((prev) => (
            teacherLive
              ? mergeTeacherLiveRecords(prev, normalized, getLocalYMD(), pendingIds, pendingDeletedIds)
              : mergeServerSnapshotRecords(prev, normalized, pendingIds, pendingDeletedIds)
          )); 
        }
      } catch (err) {
        console.warn("서버에서 기록을 불러오지 못했습니다.", err);
        if (teacherLive) return;
      }
    };

    const fetchRoster = async () => {
      try {
        const res = await fetchWithTimeout(ROSTER_API_URL);
        const data = await res.json();
        
        if (Array.isArray(data)) {
          setStudents(data); 
          const newMaxSlots = { '1반': 24, '2반': 24, '3반': 24, '4반': 24, '5반': 24, '6반': 24 };
          data.forEach(s => { const sNum = parseInt(s.num, 10); if (sNum > newMaxSlots[s.cls]) newMaxSlots[s.cls] = sNum; });
          setMaxSlots(newMaxSlots);
        }
      } catch (err) {
        console.warn("서버에서 명단을 불러오지 못했습니다.", err);
      }
    };

    fetchRecords();
    fetchRoster();

    let intervalId;
    if (role === 'teacher' && modalState.type !== 'edit') {
      intervalId = setInterval(() => {
        fetchRecords({ teacherLive: true });
      }, 5000);
    } else if (role === 'student') {
      intervalId = setInterval(() => {
        fetchRecords();
      }, 5000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [applyRecords, getPendingRecordSets, role, modalState.type]); 

  useEffect(() => { setRosterDraft(students); }, [students]);

  useEffect(() => {
    try {
      const savedProfile = localStorage.getItem(STUDENT_PROFILE_STORAGE_KEY);
      if (!savedProfile) return;
      const parsed = JSON.parse(savedProfile);
      if (!parsed?.cls || !parsed?.num || !parsed?.name) return;
      setRememberStudentProfile(true);
      setSubmitForm((prev) => ({ ...prev, cls: parsed.cls, num: String(parsed.num), name: parsed.name }));
      setStatusForm({ cls: parsed.cls, num: String(parsed.num), name: parsed.name });
    } catch (err) {
      localStorage.removeItem(STUDENT_PROFILE_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (rememberStudentProfile) return;
    clearRememberedStudentProfile();
  }, [clearRememberedStudentProfile, rememberStudentProfile]);

  useEffect(() => {
    if (recapModal.show && recapSlide < 5 && !isStoryPaused) {
      const timer = setTimeout(() => { setRecapSlide(s => s + 1); }, 4500);
      return () => clearTimeout(timer);
    }
  }, [recapModal.show, recapSlide, isStoryPaused]);

  useEffect(() => {
    if (!foundRecord?.id) return;
    const latestRecord = records.find((record) => record.id === foundRecord.id) || null;
    if (!latestRecord) {
      setFoundRecord(null);
      setReturnMethod('');
      return;
    }
    if (latestRecord !== foundRecord) {
      setFoundRecord(latestRecord);
      if (latestRecord.status !== 'taken_home') {
        setReturnMethod(latestRecord.returnMethod || '');
      }
    }
  }, [records, foundRecord]);

  const toggleTheme = () => setIsDarkMode(prev => !prev);
  const nowIso = () => new Date().toISOString();
  const markRecordPending = useCallback((id, { deleted = false } = {}) => {
    pendingRecordMetaRef.current.set(id, {
      deleted,
      until: Date.now() + RECORD_SYNC_GRACE_MS,
    });
  }, []);
  const clearRecordPending = useCallback((id, { immediate = false } = {}) => {
    if (immediate) {
      pendingRecordMetaRef.current.delete(id);
      return;
    }
    const existing = pendingRecordMetaRef.current.get(id);
    if (!existing) return;
    pendingRecordMetaRef.current.set(id, {
      ...existing,
      until: Date.now() + RECORD_SYNC_GRACE_MS,
    });
  }, []);

  const handleRosterChange = (cls, num, newName) => {
    setRosterDraft(prev => {
      const newDraft = [...prev];
      const existingIdx = newDraft.findIndex(s => s.cls === cls && String(s.num) === String(num));
      if (existingIdx >= 0) {
        if (newName.trim() === '') newDraft.splice(existingIdx, 1);
        else newDraft[existingIdx].name = newName;
      } else {
        if (newName.trim() !== '') newDraft.push({ id: `${cls}-${num}`, cls, num: String(num), name: newName });
      }
      return newDraft;
    });
  };

  const addSlotToRoster = () => setMaxSlots(prev => ({ ...prev, [selectedRosterClass]: prev[selectedRosterClass] + 1 }));

  const saveRosterToServer = async () => {
    const nextStudents = rosterDraft
      .map(normalizeStudent)
      .filter((student) => student.name);
    const previousStudents = students.map(normalizeStudent);

    setStudents(nextStudents); 
    showToast(`총 ${nextStudents.length}명 명단이 임시 저장되었습니다.`);
    try {
      const previousMap = new Map(previousStudents.map((student) => [student.id, student]));
      const nextMap = new Map(nextStudents.map((student) => [student.id, student]));
      const requests = [];

      nextStudents.forEach((student) => {
        const previous = previousMap.get(student.id);
        if (!previous || previous.cls !== student.cls || previous.num !== student.num || previous.name !== student.name) {
          requests.push(
            fetchWithTimeout(`${ROSTER_API_URL}/${encodeURIComponent(student.id)}`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(student),
            })
          );
        }
      });

      previousStudents.forEach((student) => {
        if (!nextMap.has(student.id)) {
          requests.push(
            fetchWithTimeout(`${ROSTER_API_URL}/${encodeURIComponent(student.id)}`, {
              method: "DELETE",
            })
          );
        }
      });

      if (requests.length > 0) await Promise.all(requests);
      showToast(`명단 서버 저장이 완료되었습니다.`);
    } catch (err) {
      console.error("명단 서버 전송 실패", err);
      showToast("서버 저장에는 실패했지만 명단은 임시 저장되어 있습니다.");
    }
  };

  const handleSubmitChange = (e) => setSubmitForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleStatusChange = (e) => setStatusForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const executeSubmission = async (newRecord) => {
    if (isSubmitting) return false;
    setIsSubmitting(true);
    markRecordPending(newRecord.id);
    try {
      await fetchWithTimeout(API_URL, { 
        method: "POST", 
        headers: { "Content-Type": "application/json" }, 
        body: JSON.stringify(newRecord) 
      });
      
      applyRecords(prev => [...prev, newRecord]);
      setSubmitForm({ cls: '', num: '', name: '', purposeType: '', detail: '', duration: '', isPersonalPad: false }); 
      saveRememberedStudentProfile(newRecord);
      
      showToast('신청이 완료되었습니다! ✅');
      
      clearRecordPending(newRecord.id);
      return true;
    } catch (error) {
      clearRecordPending(newRecord.id, { immediate: true });
      console.error("서버 전송 실패:", error);
      showToast('네트워크 오류로 제출에 실패했어요. 인터넷 연결을 확인해 주세요.');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAttemptSubmit = async () => {
    if (isSubmitting) return;
    
    const { cls, num, name, purposeType, detail, duration, isPersonalPad } = submitForm;
    if (!cls || !num || !name?.trim() || !purposeType || !detail?.trim() || !duration) { showToast('⚠️ 필수 항목을 모두 입력해 주세요'); return; }
    
    const cleanName = name.trim().toLowerCase();
    const isCheatCode = cleanName.includes('由ъ벙');
    const realName = cleanName.replace(/由ъ벙/i, '');

    if (students?.length > 0 && !isCheatCode) {
      const isValidStudent = students.some(s => s.cls === cls && String(s.num) === String(num) && s.name?.trim().toLowerCase() === cleanName);
      if (!isValidStudent) { showToast('등록된 명단과 일치하지 않는 학생 정보입니다. 반, 번호, 이름을 다시 확인해 주세요.'); return; }
    }

    const todayStrLocal = getLocalYMD();
    const studentRecords = records.filter(r => doesRecordMatchStudent(r, cls, num, realName));
    
    const activeRecord = studentRecords.find(r => isActiveRecordStatus(r.status));
    const returnedToday = studentRecords.find(r => r.status === 'returned' && getYMDFromIso(r.submittedAt) === todayStrLocal);

    if (!isCheatCode) {
      if (activeRecord) {
        showToast('이미 사용 중이거나 승인 대기 중인 기록이 있습니다. 먼저 반납을 완료해 주세요.');
        return;
      }
      if (returnedToday) {
        showToast('오늘 이미 패드 사용과 반납을 완료했습니다. 내일 다시 신청해 주세요.');
        return;
      }
    }

    const finalDetail = JSON.stringify({ text: detail.trim() });
    const generatedId = crypto.randomUUID ? crypto.randomUUID() : (Date.now().toString() + '-' + Math.random().toString(36).substring(2, 9));

    const newRecord = normalizeRecord({ 
      id: generatedId, 
      cls, num, name: realName, purposeType, detail: finalDetail, 
      duration, status: 'pending', returnMethod: null, isPersonalPad, 
      submittedAt: nowIso(), updatedAt: null, returnedAt: null, editHistory: [] 
    });

    const todayObj = new Date();
    const m = todayObj.getMonth() + 1; const d = todayObj.getDate();
    const isRecapDay = (m === 6 && d === 1) || (m === 9 && d === 1) || isCheatCode;

    if (isRecapDay && !recapModal.show) {
      const pastRecords = records.filter(r => r.status === 'returned' && r.cls === cls && String(r.num) === String(num) && r.name?.trim().toLowerCase() === realName);
      
      if (pastRecords.length > 0) {
        let totalMs = 0; 
        let durationCount = {};
        let subjectCount = { '국어':0, '수학':0, '영어':0, '사회':0, '과학':0, '역사':0, '기타':0 };
        let texts = [];

        pastRecords.forEach(r => {
          if (r.submittedAt && r.returnedAt) totalMs += (new Date(r.returnedAt) - new Date(r.submittedAt));
          durationCount[r.duration] = (durationCount[r.duration] || 0) + 1;

          const parsed = parseDetail(r.detail);
          if (parsed.text && parsed.text.length > 3) texts.push(parsed.text);
          
          const subj = analyzeSubject(parsed.text); subjectCount[subj]++;
        });

        let userTimes = {};
        records.filter(r => r.status === 'returned').forEach(r => {
          const key = `${r.cls} ${r.name}`;
          if(r.submittedAt && r.returnedAt) userTimes[key] = (userTimes[key] || 0) + (new Date(r.returnedAt) - new Date(r.submittedAt));
        });
        const ranking = Object.entries(userTimes).sort((a,b) => b[1] - a[1]).map(e => ({ name: e[0], ms: e[1] }));
        const myRankIndex = ranking.findIndex(x => x.name === `${cls} ${realName}`);
        const top3 = ranking.slice(0,3);

        setRecapSlide(0); 
        setRecapModal({
          show: true,
          pendingRecord: newRecord,
          data: { count: pastRecords.length, totalMs, season: m < 7 ? '상반기' : '하반기', subjectCount, durationCount, texts, ranking: top3, myRank: myRankIndex + 1 }
        });
        return; 
      } else if (isCheatCode) {
         showToast('과거 반납 기록이 부족해서 리캡을 보여드릴 수 없어요.'); return;
      }
    }
    await executeSubmission(newRecord);
  };

  const findRecord = useCallback(() => {
    const { cls, num, name } = statusForm;
    if (!cls || !num || !name?.trim()) { showToast('⚠️ 모두 입력해 주세요'); return; }

    const sortByLatest = (a, b) => {
      const timeA = a.updatedAt || a.submittedAt || '';
      const timeB = b.updatedAt || b.submittedAt || '';
      return new Date(timeB).getTime() - new Date(timeA).getTime();
    };

    const exactCandidates = records
      .filter(r => doesRecordMatchStudent(r, cls, num, name))
      .sort(sortByLatest);

    const candidates = (exactCandidates.length > 0
      ? exactCandidates
      : records.filter(r => r.cls === cls && String(r.num) === String(num))
    )
      .sort((a, b) => {
        return sortByLatest(a, b);
      });

    const rec = candidates.find(r => ['pending', 'taken_home'].includes(r.status)) || null;

    if (!rec) { 
      showToast('현재 상태를 변경할 수 있는 기록이 없습니다. 사용 중이거나 집에 가져간 기록만 조회됩니다.'); 
      setFoundRecord(null); 
      return; 
    }
    setFoundRecord(rec); 
    setReturnMethod(rec.status === 'taken_home' ? '' : (rec.returnMethod || ''));
    saveRememberedStudentProfile({ cls, num, name: name.trim() });
  }, [records, saveRememberedStudentProfile, showToast, statusForm]);

  const updateStatus = async () => {
    if (!foundRecord) { showToast('조회된 기록이 없습니다.'); return; }
    if (!returnMethod) { showToast('⚠️ 반납 방법을 선택해 주세요'); return; }
    const t = nowIso(); 
    
    const isDirectReturn = ['감독교사 직접 반납', '담임교사 직접 반납'].includes(returnMethod);
    const isTakenHome = returnMethod === '통학생 집으로 가져가기';
    const newStatus = isTakenHome ? 'taken_home' : (isDirectReturn ? 'awaiting_approval' : 'returned');

    let recordToUpdate = null;
    const updatedRecords = records.map(r => {
      if (r.id === foundRecord.id) {
        const historyObj = r.status !== newStatus ? { field: 'returnMethod', old: r.returnMethod, new: returnMethod, at: t } : null;
        recordToUpdate = {
          ...r,
          returnMethod,
          status: newStatus,
          updatedAt: t,
          returnedAt: newStatus === 'returned' ? t : null,
          editHistory: historyObj ? [...(r.editHistory || []), historyObj] : (r.editHistory || [])
        };
        return recordToUpdate;
      }
      return r;
    });
    
    applyRecords(updatedRecords);
    if (isTakenHome) {
      setFoundRecord(null);
      setReturnMethod('');
      setStudentNoticeModal({
        show: true,
        title: '처리 완료',
        message: '집으로 가져가기가 기록되었습니다. 나중에 학교에 실제로 반납할 때 상태 변경에서 다시 조회해 최종 반납을 기록해 주세요.'
      });
    } else if (newStatus === 'returned') {
      setFoundRecord(null);
      setReturnMethod('');
    } else {
      setFoundRecord(recordToUpdate);
    }
    markRecordPending(foundRecord.id);
    showToast(
      isTakenHome
        ? '통학생 집으로 가져가기로 기록했습니다.'
        : (isDirectReturn ? '선생님 승인 대기 상태로 변경되었습니다.' : '반납 완료가 기록되었습니다')
    );
    
    if (recordToUpdate) {
      try {
        await executeSafePut(recordToUpdate, foundRecord);
        clearRecordPending(foundRecord.id);
      } catch (e) {
        clearRecordPending(foundRecord.id, { immediate: true });
        showToast('⚠️ 서버 동기화 실패');
      }
    }
  };

  const approveReturn = async (id) => {
    const teacherName = approverNames[id];
    if (!teacherName || !teacherName.trim()) { showToast('확인 교사 성함을 입력해 주세요.'); return; }
    const oldRecord = records.find(r => r.id === id); 
    
    const t = nowIso(); let recordToUpdate = null;
    const updatedRecords = records.map(r => {
      if (r.id === id) {
        recordToUpdate = { ...r, status: 'returned', returnedAt: t, updatedAt: t, editHistory: [...(r.editHistory || []), { field: 'approval', teacher: teacherName.trim(), at: t }] };
        return recordToUpdate;
      }
      return r;
    });

    applyRecords(updatedRecords);
    markRecordPending(id);
    setApproverNames(prev => { const newObj = {...prev}; delete newObj[id]; return newObj; });
    showToast('반납을 최종 확인했습니다.');
    
    if (recordToUpdate) {
      try {
        await executeSafePut(recordToUpdate, oldRecord);
        clearRecordPending(id);
      } catch (e) {
        clearRecordPending(id, { immediate: true });
        showToast('⚠️ 서버 동기화 실패');
      }
    }
  };

  async function markAsOverdue(id) {
    const oldRecord = records.find(r => r.id === id);
    if (!oldRecord) return;
    if (!isActiveRecordStatus(oldRecord.status)) {
      showToast('사용중 또는 미종결 상태에서만 미반납 처리할 수 있습니다.');
      return;
    }
    if (!window.confirm('이 기록을 미반납으로 처리할까요?')) return;

    const t = nowIso();
    const recordToUpdate = {
      ...oldRecord,
      status: 'overdue',
      updatedAt: t,
      returnedAt: null,
      editHistory: [...(oldRecord.editHistory || []), { field: 'manual_overdue', at: t }]
    };

    applyRecords(prev => prev.map(r => (r.id === id ? recordToUpdate : r)));
    markRecordPending(id);
    closeModal();
    showToast('미반납으로 처리했습니다.');

    try {
      await executeSafePut(recordToUpdate, oldRecord);
      clearRecordPending(id);
    } catch (e) {
      clearRecordPending(id, { immediate: true });
      showToast('⚠️ 서버 동기화 실패');
    }
  }

  const filteredRecords = useMemo(() => {
    const sourceRecords = currentFilter === 'pending' ? unresolvedLatestRecords : records;
    let filtered = sourceRecords.filter(r => {
      if (currentFilter === 'all') return true;
      if (currentFilter === 'pending') return ['pending', 'awaiting_approval', 'taken_home', 'overdue'].includes(r.status);
      if (currentFilter === 'awaiting') return r.status === 'awaiting_approval';
      if (currentFilter === 'overdue') return r.status === 'overdue';
      if (currentFilter === 'returned') return r.status === 'returned';
      return r.cls === currentFilter;
    });
    
    return filtered.sort((a, b) => {
      let cmp = 0;
      if (listSortType === 'num') {
        const clsA = a.cls || ''; const clsB = b.cls || '';
        if (clsA !== clsB) cmp = clsA.localeCompare(clsB);
        else cmp = Number(a.num || 0) - Number(b.num || 0);
      } else {
        const timeA = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
        const timeB = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
        cmp = timeA - timeB;
      }
      return listSortOrder === 'asc' ? cmp : -cmp;
    });
  }, [records, currentFilter, listSortType, listSortOrder, unresolvedLatestRecords]);

  const openDetail = useCallback((id) => setModalState({ type: 'detail', recordId: id }), []);
  const closeModal = useCallback(() => setModalState({ type: null, recordId: null }), []);
  
  const openEdit = useCallback((id) => { 
    const rec = records.find(r => r.id === id); 
    if (rec) { 
      const parsed = parseDetail(rec.detail);
      setEditForm({ purposeType: rec.purposeType || '', detail: parsed.text || '', duration: rec.duration || '' }); 
      setModalState({ type: 'edit', recordId: id }); 
    } 
  }, [records]);

  const saveEdit = async () => {
    const oldRecord = records.find(r => r.id === modalState.recordId); 
    if (!oldRecord) return;
    const t = nowIso(); const { purposeType, detail, duration } = editForm;
    let newHistory = [...(oldRecord.editHistory || [])];
    
    const finalDetail = JSON.stringify({ text: detail.trim() });

    if (oldRecord.purposeType !== purposeType || oldRecord.detail !== finalDetail || oldRecord.duration !== duration) newHistory.push({ field: 'content', at: t });
    
    let recordToUpdate = null;
    const updatedRecords = records.map(r => {
      if (r.id === modalState.recordId) { recordToUpdate = { ...r, purposeType, detail: finalDetail, duration, updatedAt: t, editHistory: newHistory }; return recordToUpdate; } return r;
    });
    
    applyRecords(updatedRecords);
    markRecordPending(modalState.recordId);
    closeModal(); 
    showToast('수정되었습니다.');
    
    if (recordToUpdate) {
      try {
        await executeSafePut(recordToUpdate, oldRecord);
        clearRecordPending(modalState.recordId);
      } catch (e) {
        clearRecordPending(modalState.recordId, { immediate: true });
        showToast('⚠️ 서버 동기화 실패');
      }
    }
  };

  const deleteRecord = useCallback(async (id) => {
    if (!window.confirm('이 기록을 삭제하시겠습니까?')) return;
    markRecordPending(id, { deleted: true });
    applyRecords(prev => prev.filter(r => r.id !== id));
    setFoundRecord(prev => (prev?.id === id ? null : prev));
    setReturnMethod(prev => (foundRecord?.id === id ? '' : prev));
    closeModal(); 
    showToast('삭제됨');
    try {
      await fetchWithTimeout(`${API_URL}/${id}`, { method: "DELETE" });
      clearRecordPending(id);
    } catch (e) {
      clearRecordPending(id, { immediate: true });
      showToast('⚠️ 삭제 내용이 서버에 반영되지 않았습니다.');
    }
  }, [applyRecords, clearRecordPending, closeModal, foundRecord, markRecordPending, showToast]);

  const renderStatusBadge = (rec) => {
    if (rec.status === 'returned') return <span className="status-badge status-returned"><span className="dot dot-returned"></span>{rec.isPersonalPad ? '사용종료' : '반납 완료'}</span>;
    if (rec.status === 'awaiting_approval') return <span className="status-badge status-awaiting"><span className="dot dot-awaiting"></span>승인 대기중</span>;
    if (rec.status === 'taken_home') return <span className="status-badge status-taken-home"><span className="dot dot-taken-home"></span>집에 가져감</span>;
    if (rec.status === 'overdue') return <span className="status-badge status-overdue"><span className="dot dot-overdue"></span>미반납</span>;
    return <span className="status-badge status-pending"><span className="dot dot-pending"></span>사용중</span>;
  };

  const floaters = useMemo(() => {
    if (!recapModal.data) return [];
    const { texts = [] } = recapModal.data;

    if (texts.length === 0) return [];
    const mems = [...texts].sort(() => 0.5 - Math.random()).slice(0, 15);
    return mems.map((m) => ({
      text: m,
      left: `${Math.random() * 80 + 5}%`,
      delay: `${Math.random() * 5}s`,
      dur: `${12 + Math.random() * 10}s`
    }));
  }, [recapModal.data]);

  const memoizedRanking = useMemo(() => {
    if (!recapModal?.data?.ranking || !Array.isArray(recapModal.data.ranking)) return [];
    return recapModal.data.ranking.map((r, idx) => ({
      ...r,
      uniqueKey: r.name ? `${r.name}-${idx}` : `rank-${idx}`,
      formattedTime: formatDurationText(r.ms || 0)
    }));
  }, [recapModal?.data?.ranking]);

  const myTotalTimeFormatted = useMemo(() => {
    return formatDurationText(recapModal?.data?.totalMs || 0);
  }, [recapModal?.data?.totalMs]);

  const availableTabs = role === 'student' 
    ? [ { id: 'submit', label: '신청하기' }, { id: 'status', label: '상태 변경' } ]
    : [ { id: 'dashboard', label: '한눈에 보기' }, { id: 'approvals', label: '반납 승인', badge: teacherApprovalCount }, { id: 'list', label: '신청 현황', badge: totalPendingCount }, { id: 'stats', label: '통계 분석' }, { id: 'roster', label: '명단 관리' } ];

  return (
    <div className={`app-container ${isDarkMode ? 'dark-mode' : ''}`}>
      <style dangerouslySetInnerHTML={{ __html: globalStyles }} />
      <div className="app-header">
        <div className="app-icon">📱</div>
        <div style={{ flex: 1 }}>
          <div className="app-title">패드 사용 관리<span className="role-tag">{role === 'student' ? '학생용' : '선생님용'}</span></div>
          <div className="app-subtitle">야자 태블릿 대여 신청 · 반납 시스템</div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {/* 사용법 안내 페이지로 이동하는 버튼 */}
          <a href="https://pad-des.dajung.dev/" target="_blank" rel="noopener noreferrer" className="icon-btn" style={{ textDecoration: 'none' }} title="사용법 안내">📖</a>
          <button className="icon-btn" onClick={toggleTheme} title="테마 변경">{isDarkMode ? '🌙' : '☀️'}</button>
        </div>
      </div>
      <div className="tabs">
        {availableTabs.map(tab => (
          <button key={tab.id} className={`tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
            {tab.label}{typeof tab.badge === 'number' && tab.badge > 0 && <span className="tab-badge">{tab.badge}</span>}
          </button>
        ))}
      </div>

      {activeTab === 'submit' && role === 'student' && (
        <div className="view">
          <div className="alert alert-info">반, 번호, 이름을 입력하고 패드 사용 계획을 작성해 주세요.</div>
          <div className="card">
            <div className="section-title">학생 정보</div>
            <div className="row2">
              <div>
                <label>반</label>
                <select name="cls" value={submitForm.cls} onChange={handleSubmitChange} disabled={isSubmitting}>
                  <option value="">선택</option>{['1반','2반','3반','4반','5반','6반'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><label>번호</label><input type="number" name="num" value={submitForm.num} onChange={handleSubmitChange} placeholder="예: 15" min="1" disabled={isSubmitting} /></div>
            </div>
            <label>이름</label>
            <input type="text" name="name" value={submitForm.name} onChange={handleSubmitChange} placeholder="이름 입력" disabled={isSubmitting} />
            <div className="option-grid">
              <label className={`option-chip ${rememberStudentProfile ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={rememberStudentProfile}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setRememberStudentProfile(checked);
                    if (checked) rememberCurrentStudentProfile();
                  }}
                  disabled={isSubmitting}
                />
                <span className="option-chip-mark"></span>
                <span className="option-chip-body">
                  <span className="option-chip-title">학생 정보 기억하기</span>
                  <span className="option-chip-sub">이 기기에서 반, 번호, 이름을 다음에도 자동으로 채워줘요.</span>
                </span>
              </label>
              <label className={`option-chip ${submitForm.isPersonalPad ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  name="isPersonalPad"
                  checked={submitForm.isPersonalPad}
                  onChange={(e) => setSubmitForm(prev => ({...prev, isPersonalPad: e.target.checked}))}
                  disabled={isSubmitting}
                />
                <span className="option-chip-mark"></span>
                <span className="option-chip-body">
                  <span className="option-chip-title">개인 패드 사용</span>
                  <span className="option-chip-sub">학교 태블릿이 아니라 본인 기기로 공부할 때 선택해 주세요.</span>
                </span>
              </label>
            </div>
          </div>
          <div className="card">
            <div className="section-title">사용 계획</div>
            <label>사용 목적</label>
            <select name="purposeType" value={submitForm.purposeType} onChange={handleSubmitChange} disabled={isSubmitting}>
              <option value="">용도 선택</option><option>수업 관련 검색 및 자료 조사</option><option>인강 수강</option><option>독서 및 전자책</option><option>발표 자료 제작</option><option>학습 앱 사용</option><option>기타 학습 목적</option>
            </select>
            <label>세부 계획</label>
            <textarea name="detail" value={submitForm.detail} onChange={handleSubmitChange} placeholder="구체적인 사용 계획을 입력해 주세요. (국어, 수학, 영어, 역사, 과학, 사회 중 하나라면 과목명을 포함해 적는 것을 추천합니다.)" disabled={isSubmitting}></textarea>
            
            <label>사용 시간</label>
            <select name="duration" value={submitForm.duration} onChange={handleSubmitChange} disabled={isSubmitting}>
              <option value="">선택</option><option>야자 1부</option><option>야자 2부</option><option>야자 1·2부 모두</option>
            </select>
          </div>
          <button className="btn btn-primary" onClick={handleAttemptSubmit} disabled={isSubmitting}>
            {isSubmitting ? '신청 처리 중... ⏳' : '신청 제출하기'}
          </button>
        </div>
      )}

      {activeTab === 'status' && role === 'student' && (
        <div className="view">
          <div className="alert alert-info">패드 사용 후 상태를 변경해 주세요. 통학생이 집으로 가져가기를 선택했다면 실제 반납할 때 다시 조회해서 반납 완료를 기록하면 됩니다.</div>
          <div className="card">
            <div className="section-title">본인 확인</div>
            <div className="row2">
              <div><label>반</label><select name="cls" value={statusForm.cls} onChange={handleStatusChange}><option value="">선택</option>{['1반','2반','3반','4반','5반','6반'].map(c => <option key={c} value={c}>{c}</option>)}</select></div>
              <div><label>번호</label><input type="number" name="num" value={statusForm.num} onChange={handleStatusChange} placeholder="번호" min="1" /></div>
            </div>
            <label>이름</label><input type="text" name="name" value={statusForm.name} onChange={handleStatusChange} placeholder="이름" />
            <div className="option-grid">
              <label className={`option-chip ${rememberStudentProfile ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={rememberStudentProfile}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setRememberStudentProfile(checked);
                    if (checked) rememberCurrentStudentProfile({ cls: statusForm.cls, num: statusForm.num, name: statusForm.name });
                  }}
                />
                <span className="option-chip-mark"></span>
                <span className="option-chip-body">
                  <span className="option-chip-title">학생 정보 기억하기</span>
                  <span className="option-chip-sub">다음에도 같은 기기에서 본인 확인 정보가 자동으로 채워집니다.</span>
                </span>
              </label>
            </div>
            <button className="btn btn-outline" style={{ marginTop: '16px', width: '100%', borderColor: 'var(--accent)', color: 'var(--accent)' }} onClick={findRecord}>조회하기</button>
          </div>
          {foundRecord && (
            <div>
              <div className="card" style={{ borderColor: 'var(--accent)', borderWidth: '1.5px', background: 'var(--accent-bg)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div>
                    <div className="record-name" style={{ color: 'var(--accent-text)' }}>{foundRecord.name}{foundRecord.isPersonalPad && <span style={{ fontSize:'11px', marginLeft:'6px', background:'var(--accent)', color:'white', padding:'2px 6px', borderRadius:'10px' }}>개인패드</span>}</div>
                    <div className="record-meta" style={{ opacity: 0.8 }}>{foundRecord.cls} {foundRecord.num}번 · {foundRecord.duration}</div>
                  </div>
                  <div>{renderStatusBadge(foundRecord)}</div>
                </div>
                <div style={{ fontSize: '14px', color: 'var(--accent-text)', fontWeight: 500, whiteSpace: 'pre-wrap' }}>{foundRecord.purposeType} · {parseDetail(foundRecord.detail).text}</div>
              </div>
              <div className="card">
                <div className="section-title">{foundRecord.isPersonalPad ? '사용 종료 확인' : (foundRecord.status === 'taken_home' ? '최종 반납 방법 선택' : '반납 방법 선택')} <span style={{ color: 'var(--danger-text)' }}>*필수</span></div>
                <div className="return-options">
                  {foundRecord.isPersonalPad ? (
                    <label className={`return-option selected`} style={{ padding: '16px' }}><input type="radio" name="returnMethod" value="개인 패드 사용 종료" checked readOnly /><span className="return-option-text">개인 패드 사용 종료</span></label>
                  ) : (
                    (foundRecord.status === 'taken_home'
                      ? ['감독교사 직접 반납', '담임교사 직접 반납', '태블릿 보관함 반납']
                      : ['감독교사 직접 반납', '담임교사 직접 반납', '태블릿 보관함 반납', '통학생 집으로 가져가기']
                    ).map((opt) => (
                      <label key={opt} className={`return-option ${returnMethod === opt ? 'selected' : ''}`} style={{ padding: '16px', justifyContent: 'flex-start' }}><input type="radio" name="returnMethod" value={opt} checked={returnMethod === opt} onChange={(e) => setReturnMethod(e.target.value)} /><span className="return-option-text">{opt}</span></label>
                    ))
                  )}
                </div>
                <button className="btn btn-success" style={{ width: '100%', padding: '16px 20px', marginTop: '20px', fontSize: '16px' }} onClick={() => { if(foundRecord.isPersonalPad) setReturnMethod('개인 패드 사용 종료'); updateStatus(); }}>✓ {foundRecord.isPersonalPad ? '사용 종료 기록' : (returnMethod === '통학생 집으로 가져가기' ? '집으로 가져가기 기록' : (['감독교사 직접 반납', '담임교사 직접 반납'].includes(returnMethod) ? '선생님께 반납 요청' : '반납 완료 기록'))}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'dashboard' && role === 'teacher' && (
        <div className="view-wide">
          <div className="dash-toolbar">
            <div className="dash-toolbar-group">
              <input type="date" value={dashDate} onChange={(e) => setDashDate(e.target.value)} style={{ padding: '8px 12px', width: 'auto', border: '1px solid var(--border)' }} />
              {dashDate === getLocalYMD() && <span style={{ fontSize:'12px', fontWeight: 800, background: 'var(--danger)', color: 'white', padding: '4px 8px', borderRadius: '12px' }}>오늘</span>}
            </div>
            <div className="dash-toolbar-group">
              <div style={{ display: 'flex', background: 'var(--surface2)', padding: '4px', borderRadius: '24px', border: '1px solid var(--border)' }}><button className={`dash-toggle-btn ${dashSort === 'num' ? 'active' : ''}`} style={{ border: 'none' }} onClick={() => setDashSort('num')}>번호순</button><button className={`dash-toggle-btn ${dashSort === 'applied' ? 'active' : ''}`} style={{ border: 'none' }} onClick={() => setDashSort('applied')}>신청순</button></div>
              <div style={{ display: 'flex', background: 'var(--surface2)', padding: '4px', borderRadius: '24px', border: '1px solid var(--border)' }}><button className={`dash-toggle-btn ${dashDisplay === 'num' ? 'active' : ''}`} style={{ border: 'none' }} onClick={() => setDashDisplay('num')}>번호로 표시</button><button className={`dash-toggle-btn ${dashDisplay === 'name' ? 'active' : ''}`} style={{ border: 'none' }} onClick={() => setDashDisplay('name')}>이름 표시</button></div>
              <div style={{ display: 'flex', background: 'var(--surface2)', padding: '4px', borderRadius: '24px', border: '1px solid var(--border)' }}><button className={`dash-toggle-btn ${!dashShowStatus ? 'active' : ''}`} style={{ border: 'none' }} onClick={() => setDashShowStatus(false)}>기본(상태 표시)</button><button className={`dash-toggle-btn ${dashShowStatus ? 'active' : ''}`} style={{ border: 'none', color: dashShowStatus ? 'white' : 'var(--accent)' }} onClick={() => setDashShowStatus(true)}>개인패드</button></div>
            </div>
          </div>
          <div className="dash-grid">
            {['1반', '2반', '3반', '4반', '5반', '6반'].map(clsName => {
              let classStudents = Array.from({ length: maxSlots[clsName] }, (_, i) => {
                const num = i + 1;
                return { num, rec: dashRecordsMap.get(`${clsName}-${num}`) };
              });
              
              if (dashSort === 'applied') { 
                classStudents.sort((a, b) => { 
                  if (a.rec && b.rec) return new Date(a.rec.submittedAt).getTime() - new Date(b.rec.submittedAt).getTime(); 
                  if (a.rec) return -1; 
                  if (b.rec) return 1; 
                  return a.num - b.num; 
                }); 
              }
              const uniqueClassCount = classStudents.filter(st => st.rec).length;
              
              return (
                <div key={clsName} className="dash-col">
                  <div className="dash-col-header">{clsName} <span style={{fontSize:'12px', color:'var(--text3)', fontWeight: 500}}>({uniqueClassCount}명</span></div>
                  <div className="dash-col-body" onWheel={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}>
                    {classStudents.map(st => {
                      const hasRec = !!st.rec; const isPersonal = hasRec && st.rec.isPersonalPad;
                      let statusClass = 'none'; let statusText = '미신청';
                      if (hasRec) {
                        if (st.rec.status === 'pending') { statusClass = 'pending'; statusText = '사용중'; }
                        else if (st.rec.status === 'awaiting_approval') { statusClass = 'awaiting'; statusText = '승인대기'; }
                        else if (st.rec.status === 'taken_home') { statusClass = 'taken_home'; statusText = '집에 가져감'; }
                        else if (st.rec.status === 'overdue') { statusClass = 'overdue'; statusText = '미반납'; }
                        else { statusClass = 'returned'; statusText = '반납완료'; }
                      }
                      let registeredName = null; if (students?.length > 0) { const studentInRoster = students.find(s => s.cls === clsName && String(s.num) === String(st.num)); if (studentInRoster) registeredName = studentInRoster.name; }
                      const displayText = dashDisplay === 'num' ? `${st.num}번` : (hasRec ? st.rec.name : (registeredName || 'none'));
                      return (
                        <div key={`st-${st.num}`} className={`dash-student ${hasRec ? 'applied' : ''} ${dashShowStatus && isPersonal ? 'highlight-personal' : ''}`} onClick={() => { if(hasRec) openDetail(st.rec.id) }} style={{ cursor: hasRec ? 'pointer' : 'default' }}>
                          <span className={`dash-st-name ${!hasRec && dashDisplay === 'name' ? 'dash-st-none' : ''}`}>{displayText}</span>
                          {!dashShowStatus && <span className={`dash-st-status ${statusClass}`}>{statusText}</span>}
                          {dashShowStatus && isPersonal && <span style={{ fontSize: '14px' }}>📱</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === 'approvals' && role === 'teacher' && (
        <div className="view">
          <div className="alert alert-info">학생이 선생님께 직접 반납했다고 요청한 내역입니다. 기기를 받았는지 확인한 뒤 승인해 주세요.</div>
          {records.filter(r => r.status === 'awaiting_approval').length === 0 ? (
            <div className="empty-state"><div className="empty-icon">📭</div><div className="empty-text">승인 대기 중인 반납 요청이 없습니다</div></div>
          ) : (
            <div className="record-list">
              {records.filter(r => r.status === 'awaiting_approval').map(r => (
                <div key={r.id} className="card" style={{ borderColor: 'var(--warn-text)', borderWidth: '2px' }}>
                  <div className="record-top"><div><div className="record-name">{r.name}</div><div className="record-meta">{r.cls} {r.num}번 · {r.duration}</div></div>{renderStatusBadge(r)}</div>
                  <div className="info-row"><span className="info-label">선택한 반납 방법</span><span className="info-value" style={{color: 'var(--warn-text)'}}>{r.returnMethod}</span></div>
                  <div className="info-row"><span className="info-label">반납 요청 시간</span><span className="info-value">{formatTime(r.updatedAt)}</span></div>
                  <div className="info-row"><span className="info-label">실제 사용 시간</span><span className="info-value" style={{color: 'var(--accent)'}}>{getUsageDuration(r.submittedAt, r.updatedAt)}</span></div>
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px dashed var(--border)' }}>
                    <label style={{ marginTop: 0, marginBottom: '8px' }}>확인 교사 성함 입력</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input type="text" placeholder="예: 홍길동 선생님" value={approverNames[r.id] || ''} onChange={(e) => setApproverNames({...approverNames, [r.id]: e.target.value})} style={{ flex: 1, background: 'var(--surface)' }} />
                      <button className="btn btn-success" style={{ margin: 0, padding: '0 24px' }} onClick={() => approveReturn(r.id)}>반납 확인</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'list' && role === 'teacher' && (
        <div className="view">
          <div className={`teacher-banner ${pendingCount > 0 ? 'visible' : ''}`} style={{ background: 'var(--danger-bg)', borderColor: 'var(--danger)' }}>
            <div className="teacher-banner-icon" style={{ animation: 'pulse-badge 2s infinite' }}>⚠️</div>
            <div>
              <div className="teacher-banner-title" style={{ color: 'var(--danger-text)' }}>오늘 미반납 학생이 {pendingCount}명 있습니다</div>
              <div className="teacher-banner-sub" style={{ color: 'var(--danger-text)' }}>{records.filter(r => r.status === 'pending' && getYMDFromIso(r.submittedAt) === todayStr).map(r => `${r.cls} ${r.num}번 ${r.name}`).join(', ')}</div>
            </div>
          </div>
          <div className="summary-grid">
            <div className="summary-card"><div className="summary-num" style={{ color: 'var(--accent)' }}>{todayUniqueCount}</div><div className="summary-label">오늘 신청 인원</div></div>
            <div className="summary-card"><div className={`summary-num ${pendingCount > 0 ? 'pulse' : ''}`} style={{ color: 'var(--danger)' }}>{pendingCount}</div><div className="summary-label">오늘 미반납</div></div>
            <div className="summary-card"><div className="summary-num" style={{ color: 'var(--success)' }}>{records.filter(r => r.status === 'returned' && getYMDFromIso(r.submittedAt) === todayStr).length}</div><div className="summary-label">오늘 반납 완료</div></div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
            <div className="filter-bar" style={{ marginBottom: 0 }}>
              {['all', 'pending', 'awaiting', 'overdue', 'returned', '1반', '2반', '3반', '4반', '5반', '6반'].map(f => (
                <button key={f} className={`filter-chip ${currentFilter === f ? 'active' : ''}`} onClick={() => setCurrentFilter(f)}>{f === 'all' ? '전체' : f === 'pending' ? '미반납 전체' : f === 'awaiting' ? '승인 대기' : f === 'overdue' ? '자동 미반납' : f === 'returned' ? '반납 완료' : f}</button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ display: 'flex', background: 'var(--surface2)', padding: '4px', borderRadius: '24px', border: '1px solid var(--border)' }}><button className={`dash-toggle-btn ${listSortType === 'num' ? 'active' : ''}`} style={{ border: 'none', padding: '6px 12px' }} onClick={() => setListSortType('num')}>번호순</button><button className={`dash-toggle-btn ${listSortType === 'time' ? 'active' : ''}`} style={{ border: 'none', padding: '6px 12px' }} onClick={() => setListSortType('time')}>시간순</button></div>
              <div style={{ display: 'flex', background: 'var(--surface2)', padding: '4px', borderRadius: '24px', border: '1px solid var(--border)' }}><button className={`dash-toggle-btn ${listSortOrder === 'asc' ? 'active' : ''}`} style={{ border: 'none', padding: '6px 12px' }} onClick={() => setListSortOrder('asc')}>오름차순</button><button className={`dash-toggle-btn ${listSortOrder === 'desc' ? 'active' : ''}`} style={{ border: 'none', padding: '6px 12px' }} onClick={() => setListSortOrder('desc')}>내림차순</button></div>
            </div>
          </div>
          <div className="record-list">
            {filteredRecords.length === 0 ? (
              <div className="empty-state"><div className="empty-icon">🗂️</div><div className="empty-text">신청 내역이 없습니다</div></div>
            ) : (
              filteredRecords.map(r => (
                <div key={r.id} className={`record-card ${['pending', 'taken_home', 'overdue'].includes(r.status) ? 'highlight-pending' : ''}`} onClick={() => openDetail(r.id)}>
                  <div className="record-top">
                    <div>
                      <div className="record-name">{r.status === 'pending' ? '🟡 ' : (r.status === 'awaiting_approval' ? '🟠 ' : (r.status === 'taken_home' ? '🏠 ' : (r.status === 'overdue' ? '🔴 ' : '')))}{r.name}{r.isPersonalPad && <span style={{ fontSize:'10px', marginLeft:'4px', background:'var(--accent)', color:'white', padding:'2px 6px', borderRadius:'10px' }}>개인</span>}<span className="period-tag">{r.duration}</span></div>
                      <div className="record-meta">{r.cls} {r.num}번</div>
                    </div>
                    <div className="record-top-side">
                      {renderStatusBadge(r)}
                      {isActiveRecordStatus(r.status) && (
                        <button
                          className="btn btn-overdue"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsOverdue(r.id);
                          }}
                        >
                          미반납 처리
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="record-purpose" style={{ whiteSpace: 'pre-wrap' }}>{r.purposeType}</div>
                  <div className="record-footer"><span className="record-time">신청: {formatTime(r.submittedAt)}</span>{r.returnedAt && <span className="record-time" style={{ color: 'var(--success)', fontWeight: 700 }}>반납: {formatTime(r.returnedAt)}</span>}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* 선생님용 통계 분석 화면 */}
      {activeTab === 'stats' && role === 'teacher' && (() => {
        const { totalCount, personalRatio, avgMs, subjectCounts, durationCounts, classTimeMs, purposeCounts } = statsData;
        const maxSubj = Math.max(...Object.values(subjectCounts), 1);
        const maxClassMs = Math.max(...Object.values(classTimeMs), 1);
        const maxPurpose = Math.max(...Object.values(purposeCounts), 1);
        
        return (
          <div className="view-wide">
             <div className="summary-grid">
               <div className="summary-card">
                 <div className="summary-num" style={{ color: 'var(--accent)' }}>{formatDurationText(avgMs)}</div>
                 <div className="summary-label">학생당 평균 사용시간</div>
               </div>
               <div className="summary-card">
                 <div className="summary-num" style={{ color: 'var(--danger)' }}>{personalRatio}%</div>
                 <div className="summary-label">개인 패드 사용 비율</div>
               </div>
               <div className="summary-card">
                 <div className="summary-num" style={{ color: 'var(--success)' }}>{totalCount}건</div>
                 <div className="summary-label">누적 총 신청 건수</div>
               </div>
             </div>

             <div className="stats-grid">
               <div className="stat-card">
                 <div className="stat-card-title">가장 많이 사용한 과목</div>
                 <div className="chart-bar-wrap" style={{ height: '200px' }}>
                   {Object.entries(subjectCounts).map(([subj, val]) => (
                     <div key={`stat-subj-${subj}`} className="chart-bar-col">
                       <span className="chart-bar-val">{val > 0 ? val : ''}</span>
                       <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', width: '100%', paddingBottom: '4px' }}>
                         <div className="chart-bar-fill" style={{ height: `${(val / maxSubj) * 100}%`, opacity: val === maxSubj && val > 0 ? 1 : 0.4 }}></div>
                       </div>
                       <span className="chart-bar-label">{subj}</span>
                     </div>
                   ))}
                 </div>
               </div>

               <div className="stat-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                 <div className="stat-card-title" style={{ width: '100%' }}>야자 시간대별 사용 비율</div>
                 <div className="recap-pie" style={{ background: makeConicGradient(durationCounts, Object.values(YAJA_COLORS)), width: '160px', height: '160px', marginBottom: '24px', marginTop: '10px' }}></div>
                 <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                   {Object.entries(durationCounts).map(([k,v]) => (
                     <div key={`stat-pie-${k}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--surface2)', borderRadius: '8px' }}>
                       <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                         <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: YAJA_COLORS[k] || '#888' }}></div>
                         <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>{k}</span>
                       </div>
                        <span style={{ fontSize: '15px', fontWeight: 800, color: YAJA_COLORS[k] || '#888' }}>{v}건</span>
                     </div>
                   ))}
                 </div>
               </div>

               <div className="stat-card">
                 <div className="stat-card-title">반별 야자 사용 시간 비교</div>
                 <div className="chart-bar-wrap" style={{ height: '200px' }}>
                   {Object.entries(classTimeMs).map(([cls, ms]) => (
                     <div key={`stat-cls-${cls}`} className="chart-bar-col">
                       <span className="chart-bar-val" style={{ fontSize: '11px', color: 'var(--text2)' }}>{ms > 0 ? Math.floor(ms/3600000)+'h' : ''}</span>
                       <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', width: '100%', paddingBottom: '4px' }}>
                         <div className="chart-bar-fill" style={{ height: `${(ms / maxClassMs) * 100}%`, background: 'linear-gradient(to top, var(--success), #A7F3D0)' }}></div>
                       </div>
                       <span className="chart-bar-label">{cls}</span>
                     </div>
                   ))}
                 </div>
               </div>

               <div className="stat-card">
                 <div className="stat-card-title">태블릿 사용 목적 TOP</div>
                 <div className="h-bar-wrap">
                   {Object.entries(purposeCounts).sort((a,b)=>b[1]-a[1]).map(([purpose, val]) => (
                     <div key={`stat-purp-${purpose}`} className="h-bar-row">
                       <div className="h-bar-label"><span>{purpose}</span> <span>{val}건</span></div>
                       <div className="h-bar-track">
                         <div className="h-bar-fill" style={{ width: `${(val / maxPurpose) * 100}%` }}></div>
                       </div>
                     </div>
                   ))}
                 </div>
               </div>
             </div>
          </div>
        );
      })()}

      {activeTab === 'roster' && role === 'teacher' && (
        <div className="view">
          <div className="alert alert-info">학생 이름을 칸에 직접 입력하거나 지우세요. 번호가 모자라면 '+ 번호 추가'를 눌러 확장할 수 있습니다. 변경 후에는 서버 저장 버튼으로 반영됩니다.</div>
          <div className="filter-bar">
            {['1반', '2반', '3반', '4반', '5반', '6반'].map(c => (
              <button key={c} className={`filter-chip ${selectedRosterClass === c ? 'active' : ''}`} onClick={() => setSelectedRosterClass(c)}>{c}</button>
            ))}
          </div>
          <div className="roster-grid">
            {Array.from({ length: maxSlots[selectedRosterClass] }, (_, i) => {
              const num = i + 1; const studentData = rosterDraft.find(s => s.cls === selectedRosterClass && String(s.num) === String(num));
              return (
                <div key={`roster-${selectedRosterClass}-${num}`} className="roster-cell"><div className="roster-cell-header">{num}번</div><input type="text" className="roster-input" placeholder="빈 자리" value={studentData ? studentData.name : ''} onChange={(e) => handleRosterChange(selectedRosterClass, num, e.target.value)} /></div>
              );
            })}
            <div className="roster-add-cell" onClick={addSlotToRoster} title="클릭하면 번호 칸이 하나 더 생깁니다">+ 번호 추가</div>
          </div>
          <button className="btn btn-primary" onClick={saveRosterToServer}>변경한 명단 서버에 저장하기</button>
        </div>
      )}

      {modalState.type && role === 'teacher' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-handle"></div>
            {modalState.type === 'detail' && (() => {
              const rec = records.find(r => r.id === modalState.recordId); if (!rec) return null;
              const parsed = parseDetail(rec.detail);
              return (
                <>
                  <div className="modal-title">{rec.name} ({rec.cls} {rec.num}번)</div>
                  <div className="modal-sub">신청 일시: {formatTime(rec.submittedAt)}</div>
                  <div className="info-row"><span className="info-label">현재 상태</span><span className="info-value">{renderStatusBadge(rec)}</span></div>
                  <div className="info-row"><span className="info-label">기기 종류</span><span className="info-value">{rec.isPersonalPad ? '📱 개인 패드' : '🏫 학교 태블릿'}</span></div>
                  <div className="info-row"><span className="info-label">사용 시간</span><span className="info-value">{rec.duration}</span></div>
                  <div className="info-row"><span className="info-label">사용 목적</span><span className="info-value">{rec.purposeType}</span></div>
                  <div className="info-row" style={{ display: 'block' }}>
                    <span className="info-label" style={{ display: 'block', marginBottom: '8px' }}>세부 계획</span>
                    <div style={{ background: 'var(--surface2)', padding: '12px', borderRadius: '8px', fontSize: '14px', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {parsed.text}
                    </div>
                  </div>
                  {rec.returnMethod && <div className="info-row"><span className="info-label">선택한 반납 방법</span><span className="info-value">{rec.returnMethod}</span></div>}
                  {rec.returnedAt && (<div className="info-row"><span className="info-label">실제 사용 시간</span><span className="info-value" style={{color: 'var(--accent)', fontSize: '16px', fontWeight: 800}}>{getUsageDuration(rec.submittedAt, rec.returnedAt)}</span></div>)}
                  {rec.editHistory && rec.editHistory.some(h => h.field === 'approval') && (<div className="info-row"><span className="info-label">확인 교사</span><span className="info-value" style={{color:'var(--accent)'}}>{rec.editHistory.find(h => h.field === 'approval').teacher}</span></div>)}
                  <div className="btn-row">
                    <button className="btn btn-outline" onClick={closeModal}>닫기</button>
                    <button className="btn btn-outline" style={{ color: 'var(--accent)', borderColor: 'var(--accent)' }} onClick={() => openEdit(rec.id)}>수정</button>
                    {isActiveRecordStatus(rec.status) && <button className="btn btn-overdue" onClick={() => markAsOverdue(rec.id)}>미반납 처리</button>}
                    <button className="btn btn-outline" style={{ color: 'var(--danger)', borderColor: 'var(--danger)', flex: 0.5 }} onClick={() => deleteRecord(rec.id)}>삭제</button>
                  </div>
                </>
              );
            })()}
            {modalState.type === 'edit' && (() => {
              const rec = records.find(r => r.id === modalState.recordId); if (!rec) return null;
              return (
                <>
                  <div className="modal-title">신청 내용 수정</div>
                  <div className="card" style={{ marginBottom: '16px', boxShadow: 'none' }}>
                    <label>사용 목적</label>
                    <select value={editForm.purposeType} onChange={(e) => setEditForm({ ...editForm, purposeType: e.target.value })}><option>수업 관련 검색 및 자료 조사</option><option>인강 수강</option><option>독서 및 전자책</option><option>발표 자료 제작</option><option>학습 앱 사용</option><option>기타 학습 목적</option></select>
                    <label>세부 계획</label>
                    <textarea value={editForm.detail} onChange={(e) => setEditForm({ ...editForm, detail: e.target.value })} placeholder="구체적인 사용 계획을 입력해 주세요. (국어, 수학, 영어, 역사, 과학, 사회라면 과목명을 그대로 적는 것을 추천합니다.)"></textarea>
                    <label>사용 시간</label><select value={editForm.duration} onChange={(e) => setEditForm({ ...editForm, duration: e.target.value })}><option>야자 1부</option><option>야자 2부</option><option>야자 1·2부 모두</option></select>
                  </div>
                  <div className="btn-row"><button className="btn btn-outline" onClick={closeModal}>취소</button><button className="btn btn-primary" onClick={saveEdit}>저장</button></div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {studentNoticeModal.show && (
        <div className="modal-overlay" onClick={() => setStudentNoticeModal({ show: false, title: '', message: '' })}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-handle"></div>
            <div className="modal-title">{studentNoticeModal.title}</div>
            <div className="modal-sub" style={{ marginBottom: '16px' }}>학생 안내</div>
            <div style={{ background: 'var(--surface2)', padding: '16px', borderRadius: '12px', fontSize: '15px', lineHeight: 1.7, color: 'var(--text2)' }}>
              {studentNoticeModal.message}
            </div>
            <div className="btn-row">
              <button className="btn btn-primary" onClick={() => setStudentNoticeModal({ show: false, title: '', message: '' })}>확인</button>
            </div>
          </div>
        </div>
      )}

      {recapModal.show && recapModal.data && (() => {
        const { count, season, subjectCount, durationCount, texts, myRank } = recapModal.data;
        const maxSubj = Math.max(...Object.values(subjectCount || {}));
        const safeName = (submitForm.name || '').replace(/由ъ벙/i, '');
        
        return (
          <div className="modal-overlay" style={{ zIndex: 1000, background: '#000000', padding: '20px' }}>
            
            {floaters?.length > 0 && (
               <div className="floating-container">
                 {floaters.map((m, i) => (
                   <div key={`floater-${i}`} className="float-item" style={{ left: m.left, animationDelay: m.delay, animationDuration: m.dur }}>
                     {m.text && <span>{m.text}</span>}
                   </div>
                 ))}
               </div>
            )}

            <div className="story-modal" 
                 onClick={e => e.stopPropagation()}
                 onMouseDown={() => setIsStoryPaused(true)}
                 onMouseUp={() => setIsStoryPaused(false)}
                 onMouseLeave={() => setIsStoryPaused(false)}
                 onTouchStart={() => setIsStoryPaused(true)}
                 onTouchEnd={() => setIsStoryPaused(false)}>
                 
               <div className="story-progress">
                 {[0, 1, 2, 3, 4, 5].map(idx => (
                   <div key={`progress-${idx}`} className="story-bar">
                     <div className={`story-fill ${idx < recapSlide ? 'completed' : idx === recapSlide ? 'active' : ''} ${isStoryPaused ? 'paused' : ''}`}></div>
                   </div>
                 ))}
               </div>

               <div style={{ position: 'absolute', top: '40px', left: 0, bottom: '100px', width: '30%', zIndex: 10 }} onClick={() => setRecapSlide(s => Math.max(0, s - 1))}></div>
               <div style={{ position: 'absolute', top: '40px', right: 0, bottom: '100px', width: '70%', zIndex: 10 }} onClick={() => setRecapSlide(s => Math.min(5, s + 1))}></div>

               {recapSlide === 0 && (
                 <div className="story-content" style={{ alignItems: 'center' }}>
                    <div className="recap-title-text" style={{ fontSize: '42px', lineHeight: 1.2 }}>
                      2026 리캡<br />{season || '상반기'}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '18px', textAlign: 'center', marginTop: '20px', lineHeight: 1.5, fontWeight: 500 }}>
                      <strong>{safeName}</strong> 학생의<br />야자 패드 사용 기록이에요
                    </div>
                 </div>
               )}

               {recapSlide === 1 && (
                 <div className="story-content" style={{ padding: '32px 0' }}>
                    <h2 style={{ fontSize: '22px', marginBottom: '24px', color: 'white', textAlign: 'center', padding: '0 24px' }}>이런 계획으로 공부했어요</h2>
                   
                   {Array.isArray(texts) && texts.length > 0 && (
                     <div className="marquee-container">
                       <div className="marquee-line">
                         {texts.concat(texts).map((t, i) => <span key={`mq1-${i}`} className="marquee-tag">{t.substring(0,20)}{t.length>20?'...':''}</span>)}
                       </div>
                       {texts.length > 2 && (
                         <div className="marquee-line" style={{ animationDuration: '25s', animationDirection: 'reverse' }}>
                           {texts.slice().reverse().concat(texts).map((t, i) => <span key={`mq2-${i}`} className="marquee-tag" style={{ background: 'rgba(59, 130, 246, 0.2)' }}>{t.substring(0,20)}{t.length>20?'...':''}</span>)}
                         </div>
                       )}
                     </div>
                   )}
                 </div>
               )}

               {recapSlide === 2 && (
                 <div className="story-content" style={{ alignItems: 'center' }}>
                    <h2 style={{ fontSize: '24px', marginBottom: '40px', color: 'white' }}>이번 학기, 얼마나 사용했을까요?</h2>
                   <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                      <div style={{ fontSize: '16px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>총 신청 횟수</div>
                      <div style={{ fontSize: '48px', fontWeight: 900, color: '#FCD34D' }}>{count || 0}회</div>
                   </div>
                   <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '16px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>누적 사용 시간</div>
                     <div style={{ fontSize: '48px', fontWeight: 900, color: '#93C5FD' }}>{myTotalTimeFormatted}</div>
                   </div>
                 </div>
               )}

               {recapSlide === 3 && (
                 <div className="story-content">
                    <h2 style={{ fontSize: '24px', marginBottom: '30px', color: 'white', textAlign: 'center' }}>어떤 과목을 제일 자주 썼을까?</h2>
                   <div className="chart-bar-wrap">
                      {['국어','수학','영어','사회','과학','역사','기타'].map(subj => {
                       const val = subjectCount?.[subj] || 0;
                       const heightPct = maxSubj === 0 ? 0 : (val / maxSubj) * 100;
                       return (
                         <div key={`chart-${subj}`} className="chart-bar-col">
                           <span className="chart-bar-val">{val > 0 ? val : ''}</span>
                           <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', width: '100%', paddingBottom: '4px' }}>
                             <div className="chart-bar-fill" style={{ height: `${val === 0 ? 0 : Math.max(2, heightPct)}%`, opacity: val === maxSubj && val > 0 ? 1 : 0.4 }}></div>
                           </div>
                           <span className="chart-bar-label" style={{ color: val === maxSubj && val > 0 ? '#FFFFFF' : 'rgba(255,255,255,0.5)' }}>{subj}</span>
                         </div>
                       )
                     })}
                   </div>
                 </div>
               )}

               {recapSlide === 4 && (
                 <div className="story-content" style={{ alignItems: 'center' }}>
                    <h2 style={{ fontSize: '24px', marginBottom: '40px', color: 'white', textAlign: 'center' }}>가장 자주 사용한 야자 시간</h2>
                   <div className="recap-pie" style={{ background: makeConicGradient(durationCount, Object.values(YAJA_COLORS)), width: '180px', height: '180px', marginBottom: '40px' }}></div>
                   
                   <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                     {Object.entries(durationCount || {}).map(([k,v]) => (
                       <div key={`pie-leg-${k}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(255,255,255,0.1)', borderRadius: '12px' }}>
                         <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                           <div style={{ width: '16px', height: '16px', borderRadius: '4px', background: YAJA_COLORS[k] || '#888' }}></div>
                           <span style={{ fontSize: '15px', color: 'white', fontWeight: 600 }}>{k}</span>
                         </div>
                         <span style={{ fontSize: '18px', fontWeight: 800, color: YAJA_COLORS[k] || '#888' }}>{v}번</span>
                       </div>
                     ))}
                   </div>
                 </div>
               )}

               {recapSlide === 5 && (
                 <div className="story-content">
                    <h2 style={{ fontSize: '24px', marginBottom: '24px', color: 'white', textAlign: 'center' }}>내 누적 사용 시간 순위</h2>
                   <div style={{ flex: 1, overflowY: 'auto', marginBottom: '20px' }}
                        onClick={(e) => e.stopPropagation()}
                        onWheel={(e) => e.stopPropagation()}
                        onTouchMove={(e) => e.stopPropagation()}>
                     {memoizedRanking.map((r, idx) => (
                       <div key={r.uniqueKey} className="recap-rank-row">
                         <div className={`rank-num ${idx===0 ? 'gold' : idx===1 ? 'silver' : 'bronze'}`}>{idx+1}</div>
                         <div className="rank-name">{r.name}</div>
                         <div className="rank-time">{r.formattedTime}</div>
                       </div>
                     ))}
                     {myRank > 3 && (
                       <>
                         <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', margin: '8px 0' }}>...</div>
                         <div className="recap-rank-row me">
                           <div className="rank-num" style={{ color: '#60A5FA' }}>{myRank}</div>
                            <div className="rank-name">나 ({safeName})</div>
                           <div className="rank-time">{myTotalTimeFormatted}</div>
                         </div>
                       </>
                     )}
                   </div>
                   
                   <div style={{ position: 'relative', zIndex: 50, pointerEvents: 'auto' }}>
                     <button className="btn btn-primary" 
                             disabled={isSubmitting}
                             style={{ padding: '20px', width: '100%', fontSize: '18px', background: isSubmitting ? '#E5E7EB' : 'white', color: isSubmitting ? '#9CA3AF' : 'black', borderRadius: '16px', boxShadow: isSubmitting ? 'none' : '0 0 20px rgba(255,255,255,0.5)', cursor: isSubmitting ? 'not-allowed' : 'pointer', transform: 'none' }} 
                             onClick={async (e) => {
                               e.stopPropagation(); 
                               if (isSubmitting || !recapModal.pendingRecord) return;
                               const isSuccess = await executeSubmission(recapModal.pendingRecord);
                               if (isSuccess) {
                                 setRecapModal({ show: false, data: null, pendingRecord: null });
                               }
                             }}>
                       {isSubmitting ? '신청 처리 중... ⏳' : '이제 신청할게! 🚀'}
                     </button>
                   </div>
                 </div>
               )}
            </div>
          </div>
        );
      })()}

      <footer className="app-footer">
        <p><strong>2026 영천고등학교 패드 사용 관리 시스템 v1.2.0</strong></p>
        <p>수집된 정보(반, 번호, 이름, 사용 기록)는 교내 운영 목적에만 사용되며 외부에 제공되지 않습니다.</p>
        <p>문의: djlove2010@naver.com</p>
      </footer>

      <div className={`toast ${toastMsg.show ? 'show' : ''}`}>{toastMsg.message}</div>
    </div>
  );
}
